import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import html2canvas from 'html2canvas';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemIcon,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  InputAdornment,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Fab,
  Snackbar,
  Collapse,
  Tooltip,
  Badge,
  Chip,
  Popover,
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  Hub as SystemIcon,
  Menu as MenuIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Chat as ChatIcon,
  List as ListIcon,
  Search as SearchIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  ContentCopy as CopyIcon,
  Flag as FlagIcon,
  Logout as LogoutIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
  Description as CsvIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Notifications as NotificationsIcon,
  Gavel as GavelIcon,
  CameraAlt as ScreenshotIcon,
  Psychology as PsychologyIcon,
  AccountCircle as AccountCircleIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Bolt as BoltIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Bookmark as BookmarkIcon,
  ArrowDropDown as ArrowDropDownIcon,
  LocalShipping as LocalShippingIcon,
  Inventory2 as Inventory2Icon,
  MoveToInbox as MoveToInboxIcon,
  BarChart as BarChartIcon,
  ExpandMore as ExpandMoreIcon,
  ViewModule as ViewModuleIcon,
  Settings as SettingsIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Check as CheckIcon,
  History as HistoryIcon,
  Warehouse as WarehouseIcon,
  FactCheck as FactCheckIcon,
  Sync as SyncIcon,
  Science as ScienceIcon,
  Schedule as ScheduleIcon,
  Calculate as CalculateIcon,
  Handshake as HandshakeIcon,
} from '@mui/icons-material';
import ChatService from '../services/ChatService';
import FlexService from '../services/FlexService';
import FirstLoginFacilitySetupModal from './FirstLoginFacilitySetupModal';
import TermsAcceptanceModal, { CURRENT_TERMS_VERSION } from './TermsAcceptanceModal';
import DocumentService from '../services/DocumentService';
import AdminService from '../services/AdminService';
import AuthService from '../services/AuthService';
import ValidationService from '../services/ValidationService';
import DashboardService from '../services/DashboardService';
import { useAuth } from '../contexts/AuthContext';
import MessageRenderer from './MessageRenderer';
import ContentTabs from './ContentTabs';
import Dashboard from './Dashboard';
import PinToDashboardDialog from './PinToDashboardDialog';
import ColumnConfigDialog from './ColumnConfigDialog';
import DriveExplorer from './DriveExplorer';
import FilePreviewDialog from './FilePreviewDialog';
// FAST Studio now opens in separate tab at /studio route
import WorkspacePanel from './workspaces/WorkspacePanel';
import WorkspaceService from '../services/WorkspaceService';
import WorkspaceMenuOverlay from './WorkspaceMenuOverlay';
import WorkspaceNavBar from './WorkspaceNavBar';
import PromptsMenuPopover from './PromptsMenuPopover';
import './workspaces/registry/registerComponents'; // registers USER_ADMIN etc. for MetadataWorkspace's CUSTOM_COMPONENT dispatch
import TaskStatusIndicator from './TaskStatusIndicator';
import useTaskStatus from '../hooks/useTaskStatus';
import useBackgroundRuns from '../hooks/useBackgroundRuns';
import BackgroundRunCard from './BackgroundRunCard';
import { useSnackbar } from '../hooks/useSnackbar';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';
import ThemeSelector from './ThemeSelector';
import FontSizeSelector from './FontSizeSelector';
import ChatTurnActivity from './ChatTurnActivity';
import { detectResponseType, getResponseText, sampleDriveData, sampleDriveDataLazy, sampleFolderContents, transformS3DataToDriveStructure, transformFolderContents, FOLDER_PLACEHOLDER_FILE } from '../utils/sampleResponses';

// Re-enabled 2026-09-28 per user request -- the "session delete doesn't
// work" note above was stale: live-tested DELETE /api/sessions/{id} directly
// (200, confirmed removed from the list) and the context-menu -> confirm-
// dialog -> handleDeleteSession wiring is intact end to end. See
// project_session_history_restored.
const SESSION_HISTORY_ENABLED = true;

// Reserved screen width (px) kept clear of the chat panel's max draggable
// width -- just enough for the collapse toggle (16px) + resize handle (6px)
// plus a small buffer, so dragging chat to its widest still leaves the
// handle reachable instead of flush against the physical screen edge (a
// real bug: at true 100% there was no way to grab it back short of a full
// page reload). See project_chat_resize_edge_margin.
const CHAT_RESIZE_EDGE_MARGIN = 32;

// General ad-hoc file attachment in chat (upload a CSV/docx for the LLM to
// discuss) is built end-to-end (backend + KB /extract) but disabled for V1 --
// sending a message with an attachment goes through the same wizard-trigger/
// agent-routing pipeline as any normal message, which misroutes it (e.g. a
// message containing "document" gets hijacked into document_upload_wizard;
// Re-enabled 2026-09-22 -- the routing bypass this was waiting on is now built:
// ChatController.chatWithFiles() calls chatProcessingService.invokeAgentInSession()
// (stays in the session's current agent, or document_agent as a fallback) instead of
// the full IntentClassifier/default_orchestrator pipeline, so an attachment can no
// longer get misrouted into an unrelated wizard or into document_agent blindly
// searching the knowledge base instead of reading the attached content. See
// docs/CHAT_FILE_ATTACHMENT_DESIGN.md's former "Known issue: routing" section.
const CHAT_FILE_UPLOAD_ENABLED = true;

// Map workspace icon names (from workspace_registry.icon) to MUI components
const WORKSPACE_ICON_MAP = {
  MoveToInbox: MoveToInboxIcon,
  Inventory2: Inventory2Icon,
  LocalShipping: LocalShippingIcon,
  Settings: SettingsIcon,
  BarChart: BarChartIcon,
  Psychology: PsychologyIcon,
  Warehouse: WarehouseIcon,
  FactCheck: FactCheckIcon,
  Sync: SyncIcon,
  Science: ScienceIcon,
  Schedule: ScheduleIcon,
  Calculate: CalculateIcon,
  ViewModule: ViewModuleIcon,
  Handshake: HandshakeIcon,
};

const AtomApp = ({ themeMode: legacyThemeMode, toggleTheme: legacyToggleTheme }) => {
  // Use new theme system
  const { colors: themeColors, isDark, themeId } = useAppTheme();
  const { user, isAuthenticated, logout, hasRole, selectedConnectedSystem: selectedSystem, selectConnectedSystem: selectSystem, needsSystemSelection, systemColors, setSystemColors, getSystemColor, warehouses, selectedWarehouse, selectWarehouse, refreshUserFromStorage } = useAuth();

  // Get current system color
  const systemColor = selectedSystem ? getSystemColor(selectedSystem.id) : themeColors.success.main;

  // Map theme colors to legacy format for gradual migration
  const colors = {
    background: themeColors.background,
    surface: themeColors.surface,
    surfaceElevated: themeColors.surfaceElevated,
    userMessageBg: themeColors.message?.user || themeColors.surfaceHover,
    border: themeColors.border.default,
    borderLight: themeColors.border.light,
    textPrimary: themeColors.text.primary,
    textSecondary: themeColors.text.secondary,
    textMuted: themeColors.text.muted,
    hoverBg: themeColors.hover.background,
    // Additional colors from new theme
    primary: themeColors.primary,
    success: themeColors.success,
    error: themeColors.error,
    warning: themeColors.warning,
    info: themeColors.info,
  };

  // Original state
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! I'm your AI assistant. How can I help you today?",
      sender: 'bot',
      timestamp: new Date().toLocaleTimeString(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  // Shell-style "walk through previous prompts" on ArrowUp/ArrowDown when the
  // box is empty -- see handleSlashMenuKeyDown. -1 = not currently navigating
  // history; historyDraftRef holds whatever was typed (usually '') before
  // navigation started, so ArrowDown past the newest entry restores it.
  const [historyIndex, setHistoryIndex] = useState(-1);
  const historyDraftRef = useRef('');
  // "/" slash-command menu. Registry is fetched once (lazily, on first "/")
  // from list_slash_commands.flex and cached -- not hardcoded here, so a new
  // workspace shows up as an option automatically after a Sync from Files.
  const [slashMenuOpen, setSlashMenuOpen] = useState(false);
  const [slashMenuStage, setSlashMenuStage] = useState('command'); // 'command' | 'workspace'
  const [slashMenuIndex, setSlashMenuIndex] = useState(0);
  const [slashCommands, setSlashCommands] = useState(null); // {commands, workspaces}
  const slashCommandsLoadingRef = useRef(false);

  // Notifications (bell icon dropdown + /notification). Backed by WMS
  // alerts via Notification.List/UnreadCount/MarkRead/MarkAllRead.
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [notificationList, setNotificationList] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [llmMode, setLlmMode] = useState('AUTO'); // 'AUTO' or 'NO_LLM'
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messageRefs = useRef({}); // Store refs to message elements for screenshot

  // New session management state
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [showSessionsInChat, setShowSessionsInChat] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedSessionForMenu, setSelectedSessionForMenu] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editDescription, setEditDescription] = useState('');
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const transcriptRef = useRef('');
  const shouldAutoSendRef = useRef(false);
  const [searchDialog, setSearchDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Was computed and thrown away (just logged + a generic "Found N results"
  // banner) -- fixed 2026-09-28 to actually render clickable results. See
  // project_search_conversations_results_fix.
  const [conversationSearchResults, setConversationSearchResults] = useState(null); // null = no search run yet

  // System selector state
  const [systemSelectorAnchor, setSystemSelectorAnchor] = useState(null);
  const hasMultipleSystems = user?.connectedSystems?.length > 1;

  // Feedback state
  const [feedbackSnackbar, setFeedbackSnackbar] = useState(false);
  const [feedbackDialog, setFeedbackDialog] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [copySnackbar, setCopySnackbar] = useState(false);
  const [flaggedMessages, setFlaggedMessages] = useState(new Set());

  // Wizard form state
  const [wizardFormValues, setWizardFormValues] = useState({});
  const [submittedWizards, setSubmittedWizards] = useState(new Set());

  // Validation state - for real-time field validation
  const [validationRules, setValidationRules] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [validatingFields, setValidatingFields] = useState({}); // Track which fields are being validated
  const [validatedFieldData, setValidatedFieldData] = useState({}); // Store API validation response data (e.g., receiveInventoryId)

  // Quick actions state - yielded from backend (IDYA principle)
  // User's saved quick menu items -- search/top-10-capping state for these
  // now lives inside PromptsMenuPopover itself (moved out of this sidebar
  // into a chat-toolbar chip). See project_prompts_moved_to_chat_panel.
  const [userQuickMenuItems, setUserQuickMenuItems] = useState([]);
  const [quickMenuSnackbar, setQuickMenuSnackbar] = useState({ open: false, message: '', severity: 'success' });
  // Add/Edit quick menu item dialog
  const [editQuickMenuDialog, setEditQuickMenuDialog] = useState(false);
  const [editingQuickMenuItem, setEditingQuickMenuItem] = useState(null);
  const [isAddingQuickMenuItem, setIsAddingQuickMenuItem] = useState(false);
  const [editQuickMenuForm, setEditQuickMenuForm] = useState({ displayValue: '', value: '' });

  // Dashboard state
  const [openDashboards, setOpenDashboards] = useState(() => DashboardService.getOpenDashboards());
  const [activeContentTab, setActiveContentTab] = useState('chat');

  // "Atom Response" workspace tab -- accumulates every table any chat message
  // has produced this session (stacking, newest last), auto-focused whenever
  // a new one arrives. Design: project_atom_response_workspace_tab memory.
  // Each entry's id is `${message.id}-${tableIndex}`, matching exactly how
  // MessageRenderer's inline "View in workspace" link computes resultId, so
  // clicking that link can scroll-to-and-highlight the exact right entry.
  const [atomResponseResults, setAtomResponseResults] = useState([]);
  const [highlightedResultId, setHighlightedResultId] = useState(null);
  const processedTableMessageIds = useRef(new Set());
  const [savedDashboards, setSavedDashboards] = useState([]);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinDialogData, setPinDialogData] = useState(null);
  const [pinDialogLoading, setPinDialogLoading] = useState(false);
  const [dashboardSnackbar, setDashboardSnackbar] = useState({ open: false, message: '' });
  const [columnConfigDialog, setColumnConfigDialog] = useState({ open: false, tableId: null, entityType: null });
  const [columnConfigSnackbar, setColumnConfigSnackbar] = useState(false);

  // Centralizes "a chat message produced a table/plan" handling in ONE place,
  // instead of touching every one of the 10+ separate call sites across this
  // file that construct a bot message (normal send, wizard submit/cancel,
  // tool-start, onboarding greeting, sample-response fallback, etc.) --
  // all of them already store the full response as `responseData`, so
  // watching `messages` here catches every one uniformly. Each table gets a
  // stable id (`${message.id}-${tableIndex}`) matching MessageRenderer's
  // inline link computation, appended (never replacing -- stacking, per
  // project_atom_response_workspace_tab); a plan entry upserts by planId
  // instead (evolving state, not an independent result). The newest one
  // found auto-focuses the Atom Response tab. processedTableMessageIds
  // prevents re-processing the same message's data again on unrelated
  // re-renders.
  //
  // Loops over the WHOLE `messages` array (not just the last one) -- a real
  // gap fixed alongside chat-history persistence: `loadSessionMessages` now
  // hydrates every historical assistant message's responseData in one
  // `setMessages(wholeHistoryArray)` call, and checking only
  // `messages[messages.length - 1]` would have silently surfaced just the
  // single most recent turn's table/plan, dropping the rest of a reopened
  // conversation's structured data. The per-message dedupe set makes this
  // loop just as cheap for the live-append case (only the new last message
  // is ever actually unprocessed).
  useEffect(() => {
    const tableEntriesToAdd = [];
    const planEntriesToUpsert = [];
    let lastWithData = null;

    for (const message of messages) {
      if (!message || message.sender !== 'bot') continue;
      if (processedTableMessageIds.current.has(message.id)) continue;

      const responseData = message.responseData;
      if (!responseData) continue;
      const tables = responseData.tables || (responseData.table ? [responseData.table] : []);
      const planStatus = responseData.planStatus;
      if (tables.length === 0 && !planStatus) continue;

      processedTableMessageIds.current.add(message.id);
      tables.forEach((table, idx) => {
        tableEntriesToAdd.push({ id: `${message.id}-${idx}`, table, timestamp: message.timestamp });
      });
      if (planStatus) {
        planEntriesToUpsert.push({
          id: `plan-${planStatus.planId || message.id}`,
          planStatus,
          timestamp: message.timestamp,
        });
      }
      lastWithData = { message, tables, planStatus };
    }

    if (tableEntriesToAdd.length === 0 && planEntriesToUpsert.length === 0) return;

    setAtomResponseResults(prev => {
      let next = [...prev, ...tableEntriesToAdd];
      for (const planEntry of planEntriesToUpsert) {
        // Upsert, keyed by planId -- a plan's step tracker updates IN PLACE
        // across turns/messages, so re-running plan_execute (or replaying a
        // whole history that touched the same plan more than once) doesn't
        // pile up duplicate steppers for the same plan.
        const existingIdx = next.findIndex(e => e.id === planEntry.id);
        if (existingIdx >= 0) {
          next = [...next];
          next[existingIdx] = planEntry;
        } else {
          next = [...next, planEntry];
        }
      }
      return next;
    });

    // Focuses the REAL right-side workspace panel's "Atom Response" tab
    // (pinned first, per project_atom_response_workspace_tab) -- not this
    // chat panel's own internal ContentTabs, which would hide chat itself.
    workspacePanelRef.current?.focusAtomResponse();
    // Newest-first focus target (by array order, so a bulk history load
    // focuses the conversation's most recent result, same as a live turn);
    // actual scrollIntoView happens in the panel's own effect once it has
    // mounted/updated with the new entry.
    if (lastWithData) {
      const { message, tables, planStatus } = lastWithData;
      if (planStatus) {
        setHighlightedResultId(`plan-${planStatus.planId || message.id}`);
      } else if (tables.length > 0) {
        setHighlightedResultId(`${message.id}-${tables.length - 1}`);
      }
    }
  }, [messages]);

  // Called from MessageRenderer's inline "View in workspace" link -- focuses
  // the Atom Response tab and scrolls/highlights the exact matching entry,
  // even if it's an older one scrolled out of view in the stack (not just
  // "open the tab generically").
  const handleViewTableInWorkspace = useCallback((resultId) => {
    workspacePanelRef.current?.focusAtomResponse();
    setHighlightedResultId(resultId);
  }, []);

  // Drive/File Explorer state
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [driveDialogOpen, setDriveDialogOpen] = useState(false);
  const [driveData, setDriveData] = useState(null);
  const [organizationUsers, setOrganizationUsers] = useState([]);

  // File Preview state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // FAST Studio state - now opens in separate tab at /studio
  // State kept for potential future use but dialog no longer used here

  // Account dialog state
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [termsReviewOpen, setTermsReviewOpen] = useState(false);
  const [accountForm, setAccountForm] = useState({
    firstName: '',
    lastName: '',
    username: '',
    tenantCode: '',
    warehouseCode: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [accountFormErrors, setAccountFormErrors] = useState({});
  const [accountSaving, setAccountSaving] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [accountSnackbar, setAccountSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Task status WebSocket hook for real-time script execution updates
  const {
    status: taskStatus,
    isConnected: taskStatusConnected,
    subscribedTaskId,
    subscribe: subscribeToTask,
    cancel: cancelTask,
    disconnect: disconnectTaskStatus,
  } = useTaskStatus();

  // Track dismissed task status indicators
  const [dismissedTaskIds, setDismissedTaskIds] = useState(new Set());

  // Chat file attachment state
  const [attachedFiles, setAttachedFiles] = useState([]);
  const chatFileInputRef = useRef(null);
  // Direct-DOM width updates during a resize drag (see handleChatResizeStart)
  // instead of calling setChatWidth on every mousemove -- ChatArea's own
  // useMemo doesn't actually memoize (its `colors` dependency is a fresh
  // object every render, already flagged by eslint-plugin-react-hooks), so
  // a setState per pixel of drag was forcing a full recompute of the whole
  // message list on every mousemove, and on a fast drag gesture React fell
  // behind badly enough that only the drag's start/end positions visibly
  // rendered -- looking exactly like the resize only ever landing at 0 or
  // 100%. See project_chat_resize_drag_perf_fix.
  const chatBoxRef = useRef(null);
  // Anchors the session-history Popover to the History icon so it overlays
  // the chat panel instead of the old inline <Collapse> pushing messages
  // down. See project_session_history_overlay_fix.
  const historyButtonRef = useRef(null);

  // Prompts picker anchor (chip in the chat toolbar) -- moved out of the
  // always-open left sidebar section, see project_prompts_moved_to_chat_panel.
  const [promptsAnchorEl, setPromptsAnchorEl] = useState(null);

  // Workspace panel state
  const workspacePanelRef = useRef(null);
  const [mobileWorkspaceVisible, setMobileWorkspaceVisible] = useState(false);
  const [studioAllowed, setStudioAllowed] = useState(false);
  useEffect(() => {
    let active = true;
    setStudioAllowed(false);
    if (user?.id) AuthService.checkStudioAccess().then(allowed => {
      if (active) setStudioAllowed(allowed);
    });
    return () => { active = false; };
  }, [user?.id, user?.connectedSystemId]);
  const [pinnedWorkspaces, setPinnedWorkspaces] = useState([]);
  // Full-tree menu: a center-top chip opens WorkspaceMenuOverlay (full-screen,
  // shows pinned items first, "All Workspaces" tab for browsing/pinning the
  // rest) instead of confining the menu to a permanently-open left-sidebar
  // section -- see project_sidebar_tab_level_pinning.
  const [workspaceMenuOpen, setWorkspaceMenuOpen] = useState(false);
  const [chatWidth, setChatWidth] = useState(() => {
    const saved = localStorage.getItem('waide_chat_width');
    const width = saved ? parseInt(saved, 10) : 480;
    // Chat can now be dragged up to (screen width - CHAT_RESIZE_EDGE_MARGIN)
    // -- the old cap existed back when there was a third sidebar column and
    // a 75% limit; both are gone now (see project_two_panel_layout,
    // project_chat_panel_full_width). The margin isn't the old cap coming
    // back -- it's just enough room (collapse toggle + resize handle width)
    // to keep the resize handle from landing flush against the physical
    // screen edge, where it's nearly impossible to grab. Real bug found
    // live: at true 100% the handle was unreachable, so there was no way
    // back except a full-page reload. See project_chat_resize_edge_margin.
    const maxWidth = window.innerWidth - CHAT_RESIZE_EDGE_MARGIN;
    return Math.max(320, Math.min(maxWidth, width));
  });
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [isResizingChat, setIsResizingChat] = useState(false);

  // Helper to extract and apply preferences from any response
  const applyPreferencesFromResponse = (response) => {
    // Handle direct preferences object
    if (response?.preferences?.systemColors) {
      setSystemColors(prev => ({
        ...prev,
        ...response.preferences.systemColors
      }));
    }

    // Handle TABLE type with key/value preference rows
    if (response?.table?.entityType === 'PREFERENCE' && selectedSystem) {
      const colorRow = response.table.rows?.find(r => r.data?.key === 'systemColor');
      if (colorRow?.data?.value) {
        setSystemColors(prev => ({
          ...prev,
          [selectedSystem.id]: colorRow.data.value
        }));
      }
    }
  };

  // Fetch pinned workspaces when authenticated
  const [workspaceGroups, setWorkspaceGroups] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchPinnedWorkspaces = async () => {
      try {
        const [pinned, groups] = await Promise.all([
          WorkspaceService.getPinnedWorkspaces(),
          WorkspaceService.getWorkspaceGroups(),
        ]);
        setPinnedWorkspaces(pinned || []);
        setWorkspaceGroups(groups || []);
      } catch (err) {
        console.error('Failed to fetch pinned workspaces:', err);
      }
    };
    fetchPinnedWorkspaces();
  }, [isAuthenticated]);

  // Chat resize handlers. `fromCollapsed` is set when the drag starts from
  // the collapsed strip (there was previously no way to grab-and-pull it
  // back open to an arbitrary width -- only a click that jumped straight to
  // the pre-collapse chatWidth). When true, this un-collapses immediately
  // and starts the drag's width calculation from 0 instead of the old
  // chatWidth, so the panel visually grows from the cursor's actual pull
  // distance rather than jumping to some unrelated previous size. See
  // project_chat_resize_from_collapsed.
  const handleChatResizeStart = useCallback((e, { fromCollapsed = false } = {}) => {
    e.preventDefault();
    if (fromCollapsed) {
      setChatCollapsed(false);
    }
    setIsResizingChat(true);
    const startX = e.clientX;
    const startWidth = fromCollapsed ? 0 : chatWidth;
    let currentWidth = startWidth;
    // Real bug found live 2026-10-06 ("I cannot click on the > icon... instead
    // its the stretch icon"): this handler fires on EVERY mousedown on the
    // collapsed strip, drag or not, since a plain click is just a mousedown+
    // mouseup with zero movement. Without this flag, a plain click (fromCollapsed,
    // no drag) left currentWidth at its startWidth of 0 all the way to mouseup,
    // which unconditionally committed setChatWidth(0) -- silently re-collapsing
    // the panel to an invisible, unreachable zero width instead of the simple
    // "click expands to the last width" behavior the collapsed strip's own title
    // text promises ("click, or drag to a specific width"). Only a real drag
    // (handleMouseMove actually firing) should ever commit a new width.
    let hasDragged = false;
    // Up to (screen width - CHAT_RESIZE_EDGE_MARGIN) -- see the note on the
    // initial-width clamp above for why the margin exists (keeps the resize
    // handle grabbable instead of flush against the screen edge).
    const maxWidth = window.innerWidth - CHAT_RESIZE_EDGE_MARGIN;

    const handleMouseMove = (moveEvent) => {
      hasDragged = true;
      // Chat is the LEFT panel now (see project_chat_on_left) with its
      // resize handle on its RIGHT edge, adjacent to the workspace panel --
      // dragging right grows chat, so delta is the mirror image of the old
      // right-panel-with-left-handle math (which grew on a leftward drag).
      const delta = moveEvent.clientX - startX;
      currentWidth = Math.max(320, Math.min(maxWidth, startWidth + delta));
      // Direct DOM write, not setChatWidth -- see the note on chatBoxRef
      // above for why a setState per pixel of drag broke this.
      if (chatBoxRef.current) {
        chatBoxRef.current.style.width = `${currentWidth}px`;
      }
    };

    const handleMouseUp = () => {
      setIsResizingChat(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      // A plain click (no drag) just expands to whatever chatWidth already is --
      // setChatCollapsed(false) above already did that; nothing else to commit.
      if (!hasDragged) return;
      // Commit to React state once, on release, so it persists through
      // re-renders and gets picked up by the initial-width clamp on reload.
      setChatWidth(currentWidth);
      localStorage.setItem('waide_chat_width', String(currentWidth));
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [chatWidth]);

  // Opening a screen from the menu, the nav bar or a chat link goes to its tab when one is open (switching to the
  // view asked for) instead of adding "Receiving (2)" beside it; a caller that wants a second copy passes true.
  const handleOpenWorkspace = useCallback((workspaceCode, workspaceTitle, navContext = null, allowMultiple = false) => {
    setMobileWorkspaceVisible(true);
    if (workspacePanelRef.current) {
      workspacePanelRef.current.openWorkspace(workspaceCode, workspaceTitle, allowMultiple, navContext);
    }
  }, []);

  // Commands the backend queued for this reply (for example after a build: open the Areas tab). They run as soon as the
  // reply arrives, without a click. Each command is {element, value} (a dotted path such as workspace.tab and what to set it to). Unknown elements are ignored, so new UI triggers can be added here without changing any agent.
  const runClientCommands = useCallback((commands) => {
    if (!Array.isArray(commands)) return;
    commands.forEach((command) => {
      if (!command || !command.element) return;
      const value = command.value || '';
      switch (command.element) {
        // A null title lets the panel use the workspace's own title, not its code.
        case 'workspace.open': {
          if (value) handleOpenWorkspace(value, null, null, false);
          break;
        }
        // CODE/view, optionally with ?field=value filters (e.g. WAREHOUSE_SETUP2/areas?areaId=FRZ01) so the
        // screen opens on what was just built, like a NAVIGATE action's filters.
        case 'workspace.tab': {
          const [path, query] = value.split('?');
          const [code, viewId] = path.split('/');
          const { search, ...rest } = query ? Object.fromEntries(new URLSearchParams(query)) : {};
          const filters = Object.keys(rest).length ? rest : null;
          const navContext = viewId || filters || search
            ? { ...(viewId ? { viewId } : {}), ...(filters ? { filters } : {}), ...(search ? { search } : {}) } : null;
          if (code) handleOpenWorkspace(code, null, navContext, false);
          break;
        }
        default:
          break;
      }
    });
  }, [handleOpenWorkspace]);

  // FH-272: work Atom runs in the background (a large build part). While it runs the chat shows it; when it ends, its
  // message (also saved in the chat history on the server) appears in the chat it belongs to, the step tracker gets
  // the final state, the screen that shows the result opens, and a notice says so wherever the user is.
  const [backgroundNotice, setBackgroundNotice] = useState({ open: false, message: '', severity: 'success' });
  const handleBackgroundRunFinished = useCallback(async (run, lastPlanStatus) => {
    const ok = run.metadata?.ok !== false && run.status === 'COMPLETED';
    const words = run.metadata?.userMessage
      || (ok ? `${run.metadata?.label || 'The background work'} is finished.` : `${run.metadata?.label || 'The background work'} stopped: ${run.errorMessage || 'no reason given'}`);
    let planStatus = lastPlanStatus;
    if (run.metadata?.planKey) {
      try {
        const res = await FlexService.executeScript({ scriptName: 'get_plan_status', context: { _payload: { planKey: run.metadata.planKey } } });
        planStatus = res?.context?.result || res?.result || planStatus;
      } catch (e) { /* keep the last known state */ }
    }
    const inThisChat = run.metadata?.sessionId && run.metadata.sessionId === currentSessionIdRef.current;
    if (inThisChat) {
      setMessages((prev) => (prev.some((m) => m.id === `bg-${run.taskId}`) ? prev : [...prev, {
        id: `bg-${run.taskId}`,
        text: words,
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString(),
        responseData: planStatus ? { response: words, planStatus } : undefined,
      }]));
      if (ok) runClientCommands(run.metadata?.onFinish);
    }
    setBackgroundNotice({
      open: true,
      severity: ok ? 'success' : 'error',
      message: inThisChat ? words : `${words} (in another conversation)`,
    });
  }, [runClientCommands]);
  const currentSessionIdRef = useRef(null);
  currentSessionIdRef.current = currentSession?.sessionId || null;
  const {
    runs: backgroundRuns,
    planStatuses: backgroundPlanStatuses,
    refresh: refreshBackgroundRuns,
    isTerminal: isBackgroundRunDone,
  } = useBackgroundRuns(currentSession?.sessionId || null, handleBackgroundRunFinished);

  // FH-272: a run that starts in this chat is brought into view under the latest message
  const activeRunsHere = backgroundRuns.filter((run) => !isBackgroundRunDone(run) && run.metadata?.sessionId
    && run.metadata.sessionId === currentSession?.sessionId).length;
  useEffect(() => {
    if (activeRunsHere > 0) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeRunsHere]);

  // FH-272: while a build runs in the background, its step tracker in the Atom Response panel follows it live
  const shownLivePlansRef = useRef(new Set());
  useEffect(() => {
    const live = Object.values(backgroundPlanStatuses || {}).filter((ps) => ps && ps.planId);
    if (live.length === 0) return;
    // the first time a run's tracker appears, bring it to the front, as a reply with a plan does
    const firstSeen = live.find((ps) => !shownLivePlansRef.current.has(ps.planId));
    if (firstSeen) {
      live.forEach((ps) => shownLivePlansRef.current.add(ps.planId));
      workspacePanelRef.current?.focusAtomResponse();
      setHighlightedResultId(`plan-${firstSeen.planId}`);
    }
    setAtomResponseResults((prev) => {
      let next = prev;
      for (const planStatus of live) {
        const id = `plan-${planStatus.planId}`;
        const idx = next.findIndex((e) => e.id === id);
        const entry = { id, planStatus, timestamp: new Date().toLocaleTimeString() };
        next = idx >= 0 ? next.map((e, i) => (i === idx ? entry : e)) : [...next, entry];
      }
      return next;
    });
  }, [backgroundPlanStatuses]);

  // Re-fetches the pinned list -- passed to WorkspaceMenuOverlay so it can
  // refresh this shell's state after any pin/unpin/reorder it performs
  // internally (it talks to WorkspaceService directly, not through handlers
  // here, since it also needs to distinguish tab-level pins by viewId).
  const refreshPinnedWorkspaces = useCallback(async () => {
    try {
      const pinned = await WorkspaceService.getPinnedWorkspaces();
      setPinnedWorkspaces(pinned || []);
    } catch (err) {
      console.error('Failed to refresh pinned workspaces:', err);
    }
  }, []);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    // Restore the most recent active session on load/refresh -- previously
    // currentSession always started null and only ever got set by an explicit
    // click on handleSelectSession, so a browser refresh silently dropped the
    // user back into an empty chat even though the full conversation was
    // still there server-side (getUserSessions already returns active
    // sessions ordered newest-first, and loadSessionMessages already existed
    // to rehydrate one -- this was purely a missing auto-trigger, not a data
    // gap). No race to guard against: `sessions` (and anything clickable in
    // the sidebar) is itself only populated once this same loadSessions()
    // call resolves, so nothing else can set currentSession first.
    (async () => {
      const sessionsData = await loadSessions();
      if (sessionsData.length > 0) {
        setCurrentSession(sessionsData[0]);
        await loadSessionMessages(sessionsData[0].sessionId);
      }
    })();
    loadUserQuickMenuItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // INTAKE_GREETING's automatic chat-bubble nudge ("looks like this
  // warehouse hasn't been set up yet...") was removed 2026-09-22, superseded
  // by FirstLoginFacilitySetupModal -- a blocking modal now enforces facility
  // setup right after login/system-selection resolves (see the
  // facilitySetup* state/effect and gate below), rather than a soft nudge
  // the user could ignore indefinitely.

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Warn before an accidental refresh/close/navigate-away while Atom is still
  // generating a response -- the browser will not let us actually BLOCK it
  // (that's a deliberate anti-abuse restriction) or show a custom message
  // (modern browsers force their own generic text), only offer the native
  // confirm dialog via preventDefault + returnValue. Registered/removed with
  // isLoading so it never fires outside an actual in-flight request.
  useEffect(() => {
    if (!isLoading) return undefined;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isLoading]);

  // Cleanup task status WebSocket on unmount
  useEffect(() => {
    return () => {
      disconnectTaskStatus();
    };
  }, [disconnectTaskStatus]);

  // Removed 2026-09-20: this used to send a literal "get my preferences" chat
  // message through the normal agent pipeline on every system selection, but
  // no backend agent has ever produced the {preferences.systemColors} or
  // {table.entityType:'PREFERENCE'} response shape applyPreferencesFromResponse
  // looks for (confirmed by searching the entire backend and seed data) --
  // this always silently fell through to default colors. Removed the dead
  // auto-fetch call itself; applyPreferencesFromResponse and systemColors
  // state are left in place since they are harmless no-ops elsewhere and
  // would work again if a real preferences feature is ever built.

  // Inject system selector message if user needs to select a system
  useEffect(() => {
    if (needsSystemSelection && user?.connectedSystems?.length > 1) {
      const systemSelectMessage = {
        id: 'system-select',
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString(),
        text: 'Please select a system to continue.',
        responseData: {
          reply: 'You have access to multiple systems. Which one would you like to work with?',
          rating: 'NORMAL',
          wizard: {
            wizardKey: 'SELECT_SYSTEM',
            status: 'AWAITING_INPUT',
            progress: { stepNumber: 1, totalSteps: 1, phaseLabel: 'Select System' },
            form: {
              formId: 'connected-system-select-form',
              title: 'Select Connected System',
              description: 'Choose the connected system you want to work with for this session.',
              fields: [{
                fieldId: 'connected_system_id',
                label: 'Connected System',
                type: 'DROPDOWN',
                required: true,
                options: user.connectedSystems.map(system => ({
                  value: system.id,
                  label: `${system.systemName} (${system.systemCode})`
                })),
                status: 'NOT_ADDRESSED',
                value: null
              }],
              actions: [
                { actionId: 'select', label: 'Continue', type: 'SUBMIT', style: 'PRIMARY' },
                { actionId: 'logout', label: 'Logout', type: 'ACTION', style: 'SECONDARY' }
              ]
            }
          }
        }
      };

      setMessages([systemSelectMessage]);
    }
  }, [needsSystemSelection, user?.connectedSystems]);

  // Blocking first-login facility setup gate. Waits for system selection to
  // resolve first (a multi-system user picks one via the chat message above,
  // which needs the shell mounted -- so this check deliberately does not run
  // until needsSystemSelection is false). null = still checking (show a
  // spinner, not the shell, to avoid a flash of the app underneath), true =
  // show the blocking modal, false = proceed normally.
  const [facilitySetupNeeded, setFacilitySetupNeeded] = useState(null);
  const [facilitySetupError, setFacilitySetupError] = useState(null);
  const [facilitySetupCheckAttempt, setFacilitySetupCheckAttempt] = useState(0);

  useEffect(() => {
    if (needsSystemSelection) {
      return;
    }
    // Real bug found live 2026-10-01: the platform admin (FOYSONIS_DEV role) got
    // stuck behind this blocking gate on FOYSONIS's own long-standing demo
    // warehouse -- every facility-setup attempt there failed (a pre-existing,
    // separately-tracked WMS bug unrelated to this gate), and with zero Area rows
    // ever created, get_current_warehouse_setup_status's own fallback check never
    // found anything to consider "already set up," so the modal kept re-triggering
    // every login with no way out -- not even to Administration to fix unrelated
    // settings. A platform admin manages OTHER tenants' onboarding; they were never
    // the audience this first-login gate was built for, so skip it for them
    // unconditionally rather than depend on this tenant ever happening to have
    // real Area data.
    if (user?.roles?.includes('FOYSONIS_DEV')) {
      setFacilitySetupNeeded(false);
      return;
    }
    // Real bug found 2026-10-01 testing a genuinely blank platform-bootstrap DB:
    // a user with ZERO connected systems (e.g. the FOYSONIS_DEV root user
    // PlatformBootstrapRunner creates -- it deliberately does not provision any
    // connected_systems row, see platform_bootstrap.flex) never gets an
    // auto-selected system (AuthContext only auto-selects when length === 1),
    // so selectedSystem?.id stays undefined forever and this effect used to
    // just `return` -- leaving facilitySetupNeeded stuck at its initial `null`
    // and the blocking gate below showing a CircularProgress forever, with no
    // error and no way out. There is nothing to set facility-setup status FOR
    // without a connected system, so treat "no systems at all" as "nothing to
    // set up" (skip the gate) rather than hanging indefinitely.
    if (!selectedSystem?.id) {
      if (!user?.connectedSystems?.length) {
        setFacilitySetupNeeded(false);
      }
      return;
    }
    let cancelled = false;
    setFacilitySetupNeeded(null);
    setFacilitySetupError(null);
    FlexService.executeScript({ scriptName: 'get_current_warehouse_setup_status' })
      .then((response) => {
        if (cancelled) return;
        if (response?.success !== true) {
          throw new Error(response?.errorMessage || 'Facility setup status check failed.');
        }
        const result = response?.context?.result;
        if (typeof result?.setupCompleted !== 'boolean' || typeof result?.onboardingCompleted !== 'boolean') {
          throw new Error('Facility setup status is unavailable.');
        }
        const setupCompleted = result.setupCompleted;
        // Empty additional warehouses are a valid admin choice after initial onboarding.
        setFacilitySetupNeeded(!(setupCompleted || result.onboardingCompleted === true));

        // Returning user (not first login, no blocking modal) -- swap the
        // generic static greeting for Atom's real login housekeeping instead
        // of leaving it as-is, per explicit user direction: "the notification
        // should tie into Atom -- when the user logs in Atom should trigger
        // general housekeeping ... provide that status/finding to the user
        // instead of a generic fixed message." atom_login_housekeeping merges
        // unread notifications (any publisher) into one message (agent memory
        // notes are agents' working notes, not shown -- FH-221); only override the
        // default greeting when there's actually something to report.
        if (setupCompleted) {
          FlexService.executeScript({ scriptName: 'atom_login_housekeeping' })
            .then((hkResponse) => {
              if (cancelled) return;
              const hk = hkResponse?.context?.result;
              if (hk?.hasFindings && hk?.message) {
                setMessages([
                  {
                    id: 1,
                    text: hk.message,
                    sender: 'bot',
                    timestamp: new Date().toLocaleTimeString(),
                  },
                ]);
              }
            })
            .catch((err) => {
              console.warn('Atom login housekeeping check failed (non-blocking):', err);
            });
        }
      })
      .catch((err) => {
        console.warn('Facility setup status check failed:', err);
        if (!cancelled) {
          setFacilitySetupNeeded(null);
          setFacilitySetupError('We could not check your facility setup. Please retry or contact your administrator.');
        }
      });
    return () => { cancelled = true; };
  }, [needsSystemSelection, selectedSystem?.id, user?.id, facilitySetupCheckAttempt]);

  const handleFacilitySetupComplete = useCallback(async () => {
    setFacilitySetupNeeded(false);

    // Required onboarding completes through a direct FLEX summary, without
    // routing a synthetic user message through the model.
    setMessages([]);
    setIsLoading(true);
    try {
      const summary = await FlexService.executeScript({ scriptName: 'FACILITY_SETUP_SUMMARY' });
      const response = summary?.context?.result;
      if (!response || typeof response.response !== 'string') {
        throw new Error('Facility summary is unavailable.');
      }
      setMessages([
        {
          id: Date.now(),
          text: response?.response || "Welcome! How can I help you today?",
          sender: 'bot',
          timestamp: new Date().toLocaleTimeString(),
          responseData: response,
        },
      ]);
      if (response?.sessionId) {
        setCurrentSession({ sessionId: response.sessionId });
      }
    } catch (err) {
      console.error('Failed to get onboarding greeting:', err);
      setMessages([
        {
          id: Date.now(),
          text: "Welcome! How can I help you today?",
          sender: 'bot',
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, selectedSystem?.id]);

  // Speech recognition setup
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setInputMessage(transcript);
        transcriptRef.current = transcript;

        // Check if this is a final result (user stopped speaking)
        const isFinal = event.results[event.results.length - 1].isFinal;
        if (isFinal) {
          shouldAutoSendRef.current = true;
        }
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
        // Auto-send if we have a final transcript
        if (shouldAutoSendRef.current && transcriptRef.current.trim()) {
          shouldAutoSendRef.current = false;
          // Wait 1.5 seconds after speech ends before sending
          setTimeout(() => {
            const sendButton = document.querySelector('[data-send-button]');
            if (sendButton && !sendButton.disabled) sendButton.click();
          }, 1500);
        }
        transcriptRef.current = '';
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        shouldAutoSendRef.current = false;
        transcriptRef.current = '';
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const toggleListening = () => {
    if (!recognitionRef.current) {
      setError('Speech recognition not supported in this browser');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadSessions = async () => {
    try {
      const sessionsData = await ChatService.getSessions();
      setSessions(sessionsData);
      return sessionsData;
    } catch (error) {
      console.error('Error loading sessions:', error);
      setError('Failed to load sessions');
      return [];
    }
  };

  const loadUserQuickMenuItems = useCallback(async () => {
    try {
      const items = await ChatService.getQuickMenuItems();
      console.log('Quick menu items from API:', items);
      // Transform to match the format expected by quick actions
      const transformed = (Array.isArray(items) ? items : []).map(item => ({
        id: item.id,
        buttonText: item.displayValue || item.buttonText,
        sentence: item.value || item.sentence,
        isUserItem: true, // Flag to identify user-saved items
        usageCount: item.usageCount || 0,
        displayOrder: item.displayOrder,
      }));
      console.log('Transformed quick menu items:', transformed);
      setUserQuickMenuItems(transformed);
    } catch (error) {
      console.error('Error loading user quick menu items:', error);
      // Silently fail - will use defaults
    }
  }, []);

  // Save a prompt to quick menu
  const handleSaveToQuickMenu = async (promptText) => {
    try {
      const result = await ChatService.addQuickMenuItem(promptText);
      if (result.success) {
        setQuickMenuSnackbar({ open: true, message: 'Saved to My Prompts!', severity: 'success' });
        loadUserQuickMenuItems(); // Refresh the list
      } else {
        setQuickMenuSnackbar({ open: true, message: result.error || 'Failed to save', severity: 'error' });
      }
    } catch (error) {
      setQuickMenuSnackbar({ open: true, message: 'Failed to save to My Prompts', severity: 'error' });
    }
  };

  // Delete a quick menu item
  const handleDeleteQuickMenuItem = useCallback(async (itemId) => {
    try {
      const result = await ChatService.deleteQuickMenuItem(itemId);
      if (result.success) {
        setQuickMenuSnackbar({ open: true, message: 'Removed from My Prompts', severity: 'success' });
        loadUserQuickMenuItems();
      }
    } catch (error) {
      setQuickMenuSnackbar({ open: true, message: 'Failed to remove item', severity: 'error' });
    }
  }, [loadUserQuickMenuItems]);

  // Open edit dialog for a quick menu item
  const handleEditQuickMenuItem = useCallback((item) => {
    setEditingQuickMenuItem(item);
    setIsAddingQuickMenuItem(false);
    setEditQuickMenuForm({
      displayValue: item.buttonText || item.displayValue || '',
      value: item.sentence || item.value || ''
    });
    setEditQuickMenuDialog(true);
  }, []);

  // Open dialog to add a new quick menu item
  const handleAddQuickMenuItem = useCallback(() => {
    setEditingQuickMenuItem(null);
    setIsAddingQuickMenuItem(true);
    setEditQuickMenuForm({ displayValue: '', value: '' });
    setEditQuickMenuDialog(true);
  }, []);

  // Save quick menu item (add or edit)
  const handleSaveQuickMenuEdit = async () => {
    if (!editQuickMenuForm.value.trim()) {
      setQuickMenuSnackbar({ open: true, message: 'Prompt text is required', severity: 'error' });
      return;
    }

    try {
      if (isAddingQuickMenuItem) {
        // Adding new item
        const result = await ChatService.addQuickMenuItem(
          editQuickMenuForm.value,
          editQuickMenuForm.displayValue || null
        );
        console.log('Add quick menu result:', result);
        if (result.success) {
          setQuickMenuSnackbar({ open: true, message: 'Quick menu item added', severity: 'success' });
          setEditQuickMenuDialog(false);
          setIsAddingQuickMenuItem(false);
          loadUserQuickMenuItems();
        } else {
          setQuickMenuSnackbar({ open: true, message: result.error || 'Failed to add item', severity: 'error' });
        }
      } else if (editingQuickMenuItem) {
        // Editing existing item
        const result = await ChatService.updateQuickMenuItem(editingQuickMenuItem.id, {
          displayValue: editQuickMenuForm.displayValue,
          value: editQuickMenuForm.value
        });
        if (result.success) {
          setQuickMenuSnackbar({ open: true, message: 'Quick menu item updated', severity: 'success' });
          setEditQuickMenuDialog(false);
          setEditingQuickMenuItem(null);
          loadUserQuickMenuItems();
        }
      }
    } catch (error) {
      setQuickMenuSnackbar({ open: true, message: isAddingQuickMenuItem ? 'Failed to add item' : 'Failed to update item', severity: 'error' });
    }
  };

  const loadSessionMessages = useCallback(async (sessionId) => {
    try {
      const messagesData = await ChatService.getSessionMessages(sessionId);
      // GET /api/sessions/{sessionId}/messages returns one row PER real message
      // ({role: "user"|"assistant"|"system", content, timestamp} -- see
      // ChatController.getSessionMessages), not a paired {userMessage, botResponse}
      // turn -- this previously assumed the wrong shape, so every restored bubble's
      // text was undefined (a real, silent bug: session restoration on refresh
      // looked broken even though the fetch itself succeeded and the right number
      // of rows came back).
      // msg.metadata (present only on assistant turns that had structured data --
      // see ChatProcessingService.buildResponseMetadata / ConversationService) is
      // the same {tables, charts, dashboard, planStatus} shape a live turn's
      // responseData carries, so it slots into `responseData` directly and the
      // existing message-watching effect below (which builds the Atom Response
      // tab) needs no special-casing to handle historical vs. live messages.
      const formattedMessages = messagesData
        .filter(msg => msg.role !== 'system')
        .map((msg, index) => ({
          id: `${sessionId}-${index}`,
          text: msg.content,
          sender: msg.role === 'user' ? 'user' : 'bot',
          timestamp: new Date(msg.timestamp).toLocaleTimeString(),
          // MessageRenderer reads a reply's text from responseData.response once
          // responseData is set, and the stored metadata has no text (FH-180).
          responseData: msg.metadata ? { response: msg.content, ...msg.metadata } : undefined,
        }));

      // The Atom Response tab shows this conversation's tables, not the previous one's (FH-180).
      setAtomResponseResults([]);
      processedTableMessageIds.current.clear();
      setMessages(formattedMessages);
    } catch (error) {
      console.error('Error loading session messages:', error);
      setError('Failed to load session messages');
    }
  }, []);

  const handleSendMessage = async () => {
    // Allow sending if there's a message OR attached files
    if (!inputMessage.trim() && attachedFiles.length === 0) return;

    // Auto-switch to chat tab when sending a message
    setActiveContentTab('chat');

    // Build user message with attachments if any
    const userMessage = {
      id: Date.now(),
      text: inputMessage || (attachedFiles.length > 0 ? `Attached ${attachedFiles.length} file(s)` : ''),
      sender: 'user',
      timestamp: new Date().toLocaleTimeString(),
      attachments: attachedFiles.length > 0 ? attachedFiles.map(f => ({
        name: f.name,
        size: f.size,
        type: f.type,
      })) : undefined,
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputMessage;
    const filesToSend = [...attachedFiles]; // Copy before clearing
    setInputMessage('');
    setHistoryIndex(-1);
    setAttachedFiles([]); // Clear attachments after sending
    setIsLoading(true);
    setError('');

    try {
      // General chat file attachment (see docs/CHAT_FILE_ATTACHMENT_DESIGN.md) --
      // CHAT_FILE_UPLOAD_ENABLED gate above.
      const response = CHAT_FILE_UPLOAD_ENABLED && filesToSend.length > 0
        ? await ChatService.sendMessageWithFiles(
            messageToSend,
            user?.id,
            currentSession?.sessionId,
            selectedSystem?.id,
            filesToSend,
            { llmMode }
          )
        : await ChatService.sendMessage(
            messageToSend,
            user?.id,
            currentSession?.sessionId,
            selectedSystem?.id,
            { llmMode }  // Pass LLM mode option
          );

      // Extract preferences if present in response
      applyPreferencesFromResponse(response);

      // Handle new FAST unified response format
      const responseType = detectResponseType(response);
      const displayText = getResponseText(response);

      const botMessage = {
        id: Date.now() + 1,
        text: displayText || response.response || 'Response received',
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString(),
        responseData: response, // Store full response for advanced rendering
        responseType: responseType,
      };

      setMessages(prev => [...prev, botMessage]);

      // UI triggers the backend queued for this reply (for example open the Areas tab after a build)
      runClientCommands(response.clientCommands);
      refreshBackgroundRuns();

      // Fetch validation rules if wizard is present
      if (response.wizard?.entity && response.wizard?.wizardKey) {
        fetchValidationRules(response.wizard.entity, response.wizard.wizardKey);
      }

      // Handle DASHBOARD response type - open as tab
      if (response.dashboard) {
        handleDashboardResponse(response.dashboard);
      }

      // Subscribe to task status updates if a task is running
      if (response.taskId) {
        // Clear any previously dismissed task status for new tasks
        setDismissedTaskIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(response.taskId);
          return newSet;
        });
        subscribeToTask(response.taskId);
      }

      // If this was a new session (no currentSession), set it from the response
      if (!currentSession && response.sessionId) {
        const newSession = { sessionId: response.sessionId, description: 'New conversation', messageCount: 1, lastUpdatedAt: Date.now(), createdAt: Date.now() };
        setSessions(prev => [newSession, ...prev]);
        if (newSession) {
          setCurrentSession(newSession);
        }
      }

    } catch (error) {
      console.error('Error sending message:', error);

      // REAL BUG FOUND 2026-10-12 (Playwright chat smoke test): a legitimate,
      // specific backend-returned error (e.g. ChatController's 403 "Start a
      // new conversation for the selected facility" when a resumed session's
      // chat_session.company_id no longer matches the currently selected
      // facility -- a real, correct guard, not an infra failure) was being
      // treated identically to "backend is completely unreachable" and
      // routed into the dev-only fake-sample-response branch below, hiding
      // the real, actionable message behind a fabricated reply. The backend
      // was reached and responded; this is never a "backend is down" case.
      // error.isBackendResponse (set in ChatService.js's handleResponse)
      // distinguishes this from a true network/transport failure. Always
      // show the real message, in every environment -- it's informative,
      // not a sign of infrastructure failure.
      // FIXED 2026-10-12: this used to fabricate a fake bot reply via
      // getRandomSampleResponse() -- in production on ANY error (a real bug:
      // a user read the fake text as a genuine diagnosis of their own
      // problem), and in development even for a legitimate, specific
      // backend-returned error (e.g. ChatController's 403 "Start a new
      // conversation for the selected facility" when a resumed session's
      // chat_session.company_id no longer matches the currently selected
      // facility -- a real, correct guard, not an infra failure). The sample
      // data and the fallback mechanism itself are deleted. error.isBackendResponse
      // (set in ChatService.js's handleResponse) distinguishes a real,
      // informative backend message from a true network/transport failure;
      // either way, the user now always sees the truth.
      setError(error.isBackendResponse ? error.message : 'Atom is temporarily unavailable. Please try again in a moment.');
    } finally {
      setIsLoading(false);
      // Focus back on input field
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleKeyPress = (event) => {
    if (slashMenuOpen) return; // slash menu owns Enter/Tab while open (see handleSlashMenuKeyDown)
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  // Fetch the "/" command registry once and cache it -- see
  // list_slash_commands.flex / SlashCommand.ListAvailable().
  const fetchSlashCommands = useCallback(async () => {
    if (slashCommands || slashCommandsLoadingRef.current) return;
    slashCommandsLoadingRef.current = true;
    try {
      const response = await FlexService.executeScript({ scriptName: 'list_slash_commands' });
      const result = response?.context?.result || { commands: [], workspaces: [] };
      setSlashCommands(result);
    } catch (e) {
      console.error('Failed to load slash commands:', e);
      setSlashCommands({ commands: [], workspaces: [] }); // avoid retrying every keystroke on failure
    } finally {
      slashCommandsLoadingRef.current = false;
    }
  }, [slashCommands]);

  // Notifications: cheap poll for the badge count, full list only fetched
  // when the dropdown is actually opened. Poll interval and on/off are
  // tenant-configurable (SYSTEM_CONFIG.NOTIFICATION_POLL_INTERVAL_MS /
  // NOTIFICATION_POLLING_ENABLED, default 10min/enabled) and come back on
  // every response so a config change takes effect on the next tick without
  // a separate settings fetch.
  const notificationPollTimeoutRef = useRef(null);

  const fetchUnreadNotificationCount = useCallback(async () => {
    try {
      const response = await FlexService.executeScript({ scriptName: 'get_unread_notification_count' });
      const result = response?.context?.result;
      setUnreadNotificationCount(result?.count || 0);
      return result;
    } catch (e) {
      console.error('Failed to fetch unread notification count:', e);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const runPoll = async () => {
      const result = await fetchUnreadNotificationCount();
      if (cancelled) return;
      // Missing/invalid config defaults to enabled @ 10 minutes.
      const pollingEnabled = result?.pollingEnabled !== false;
      const intervalMs = result?.pollIntervalMs > 0 ? result.pollIntervalMs : 600000;
      if (pollingEnabled) {
        notificationPollTimeoutRef.current = setTimeout(runPoll, intervalMs);
      }
      // If disabled, no further polling is scheduled -- the badge only
      // updates when the user opens the bell (see handleNotificationsClick).
    };

    runPoll();

    return () => {
      cancelled = true;
      if (notificationPollTimeoutRef.current) clearTimeout(notificationPollTimeoutRef.current);
    };
  }, [fetchUnreadNotificationCount]);

  const fetchNotificationList = useCallback(async () => {
    setNotificationsLoading(true);
    try {
      const response = await FlexService.executeScript({ scriptName: 'list_notifications' });
      const result = response?.context?.result;
      setNotificationList(result?.notifications || []);
    } catch (e) {
      console.error('Failed to fetch notifications:', e);
      setNotificationList([]);
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  // Opens the bell dropdown -- replaces the old behavior of sending "show me
  // my notifications" as a chat message through the LLM. Also used by the
  // /notification slash command (see handleSlashSelect) for the same
  // short-circuited, no-LLM path.
  const handleNotificationsClick = useCallback((event) => {
    setNotificationAnchorEl(event?.currentTarget || null);
    fetchNotificationList();
    fetchUnreadNotificationCount();
  }, [fetchNotificationList, fetchUnreadNotificationCount]);

  const handleMarkNotificationRead = useCallback(async (notification) => {
    // Optimistic update so the badge/list feel instant.
    setNotificationList(prev => prev.map(n => n.id === notification.id ? { ...n, isViewed: true } : n));
    setUnreadNotificationCount(prev => Math.max(0, prev - (notification.isViewed ? 0 : 1)));
    try {
      await FlexService.executeScript({
        scriptName: 'mark_notification_read',
        context: { _payload: { alertId: notification.id, source: notification.source } },
      });
    } catch (e) {
      console.error('Failed to mark notification read:', e);
    }
  }, []);

  const handleMarkAllNotificationsRead = useCallback(async () => {
    setNotificationList(prev => prev.map(n => ({ ...n, isViewed: true })));
    setUnreadNotificationCount(0);
    try {
      await FlexService.executeScript({ scriptName: 'mark_all_notifications_read' });
    } catch (e) {
      console.error('Failed to mark all notifications read:', e);
    }
  }, []);

  const handleMarkNotificationUnread = useCallback(async (notification) => {
    setNotificationList(prev => prev.map(n => n.id === notification.id ? { ...n, isViewed: false } : n));
    setUnreadNotificationCount(prev => prev + (notification.isViewed ? 1 : 0));
    try {
      await FlexService.executeScript({
        scriptName: 'mark_notification_unread',
        context: { _payload: { alertId: notification.id, source: notification.source } },
      });
    } catch (e) {
      console.error('Failed to mark notification unread:', e);
    }
  }, []);

  const handleDeleteNotification = useCallback(async (notification) => {
    setNotificationList(prev => prev.filter(n => n.id !== notification.id));
    if (!notification.isViewed) {
      setUnreadNotificationCount(prev => Math.max(0, prev - 1));
    }
    try {
      await FlexService.executeScript({
        scriptName: 'delete_notification',
        context: { _payload: { alertId: notification.id, source: notification.source } },
      });
    } catch (e) {
      console.error('Failed to delete notification:', e);
    }
  }, []);

  // "/" only activates as the very first character of the input -- a "/"
  // typed anywhere else in a genuine message is left alone. "/workspace "
  // (trailing space) transitions into stage 2, matching a workspace-name arg.
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputMessage(value);
    // Any real typing/editing exits history-walk mode (our own nav writes
    // via setInputMessage directly in handleSlashMenuKeyDown, never through
    // this onChange, so this only fires on genuine user edits).
    if (historyIndex !== -1) setHistoryIndex(-1);

    const commandMatch = /^\/(\w*)$/.exec(value);
    const workspaceArgMatch = /^\/workspace\s+(.*)$/i.exec(value);

    if (commandMatch) {
      setSlashMenuStage('command');
      setSlashMenuOpen(true);
      setSlashMenuIndex(0);
      fetchSlashCommands();
    } else if (workspaceArgMatch) {
      setSlashMenuStage('workspace');
      setSlashMenuOpen(true);
      setSlashMenuIndex(0);
      fetchSlashCommands();
    } else {
      setSlashMenuOpen(false);
    }
  };

  // Flattened workspace + view search entries for "/workspace <text>" -- one
  // entry per workspace (opens its default view) plus one entry per view
  // within it (opens that workspace focused on that view, via navContext).
  // Lets a user type "Items" and land on "Workspaces > Warehouse Setup >
  // Items" without needing to know which workspace it lives under.
  const workspaceSearchEntries = useMemo(() => {
    const entries = [];
    for (const ws of slashCommands?.workspaces || []) {
      entries.push({ code: ws.code, name: ws.name, icon: ws.icon, viewId: null, viewLabel: null });
      for (const v of ws.views || []) {
        entries.push({ code: ws.code, name: ws.name, icon: ws.icon, viewId: v.viewId, viewLabel: v.label });
      }
    }
    return entries;
  }, [slashCommands]);

  const slashMenuItems = useMemo(() => {
    if (!slashMenuOpen || !slashCommands) return [];
    if (slashMenuStage === 'command') {
      const commandMatch = /^\/(\w*)$/.exec(inputMessage);
      const filter = (commandMatch?.[1] || '').toLowerCase();
      return (slashCommands.commands || []).filter(c => c.command.toLowerCase().startsWith(filter));
    }
    const workspaceArgMatch = /^\/workspace\s+(.*)$/i.exec(inputMessage);
    const filter = (workspaceArgMatch?.[1] || '').toLowerCase();
    return workspaceSearchEntries.filter(e => (e.viewLabel || e.name)?.toLowerCase().includes(filter));
  }, [slashMenuOpen, slashCommands, slashMenuStage, inputMessage, workspaceSearchEntries]);

  const handleSlashSelect = useCallback((item) => {
    if (!item) return;

    if (slashMenuStage === 'command') {
      if (item.command === 'help') {
        // Rendered locally from the already-fetched registry -- no backend call.
        const lines = ['**Available commands:**', ...(slashCommands?.commands || []).map(
          c => `- \`${c.label}\` — ${c.description}`
        )];
        setMessages(prev => [...prev, {
          id: Date.now(),
          text: lines.join('\n'),
          sender: 'bot',
          timestamp: new Date().toLocaleTimeString(),
        }]);
        setInputMessage('');
        setSlashMenuOpen(false);
        setTimeout(() => inputRef.current?.focus(), 100);
      } else if (item.command === 'notification') {
        // Short-circuits straight to the bell dropdown -- no chat message,
        // no LLM round-trip, same as clicking the bell icon directly.
        setInputMessage('');
        setSlashMenuOpen(false);
        handleNotificationsClick({ currentTarget: inputRef.current });
      } else if (item.argType === 'WORKSPACE') {
        // Move to stage 2: prefill "/workspace " and keep the menu open,
        // now listing workspaces instead of top-level commands.
        setInputMessage(`/${item.command} `);
        setSlashMenuStage('workspace');
        setSlashMenuIndex(0);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      return;
    }

    // Stage 2: workspace (or workspace+view) selection -- pure client-side
    // navigation into the existing work center, no chat message sent.
    // allowMultiple=false: reuse an already-open tab of this workspace
    // (just refocusing its view) instead of stacking duplicate search-driven
    // tabs every time the same workspace is searched into again.
    handleOpenWorkspace(item.code, item.name, item.viewId ? { viewId: item.viewId } : null, false);
    setInputMessage('');
    setSlashMenuOpen(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [slashMenuStage, slashCommands, handleOpenWorkspace, handleNotificationsClick]);

  const handleSlashMenuKeyDown = (event) => {
    // Shell-style prompt history recall: ArrowUp on an empty box walks
    // backwards through this session's own sent prompts (oldest at the far
    // end), ArrowDown walks forward and restores the pre-nav draft past the
    // newest. Only STARTS when the box is empty, so it never steals normal
    // cursor-movement ArrowUp/Down while composing multi-line text; once
    // started it keeps working even though the box now holds history text.
    if (!slashMenuOpen && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
      const ownPrompts = messages.filter(m => m.sender === 'user' && m.text).map(m => m.text);
      if (event.key === 'ArrowUp' && (historyIndex !== -1 || inputMessage.trim() === '') && ownPrompts.length > 0) {
        event.preventDefault();
        if (historyIndex === -1) historyDraftRef.current = inputMessage;
        const nextIndex = historyIndex === -1 ? ownPrompts.length - 1 : Math.max(0, historyIndex - 1);
        setHistoryIndex(nextIndex);
        setInputMessage(ownPrompts[nextIndex]);
        return;
      }
      if (event.key === 'ArrowDown' && historyIndex !== -1) {
        event.preventDefault();
        const nextIndex = historyIndex + 1;
        if (nextIndex >= ownPrompts.length) {
          setHistoryIndex(-1);
          setInputMessage(historyDraftRef.current);
        } else {
          setHistoryIndex(nextIndex);
          setInputMessage(ownPrompts[nextIndex]);
        }
        return;
      }
    }
    if (!slashMenuOpen || slashMenuItems.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSlashMenuIndex(i => (i + 1) % slashMenuItems.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSlashMenuIndex(i => (i - 1 + slashMenuItems.length) % slashMenuItems.length);
    } else if (event.key === 'Tab' || event.key === 'Enter') {
      event.preventDefault();
      handleSlashSelect(slashMenuItems[slashMenuIndex]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setSlashMenuOpen(false);
    }
  };

  // Quick send - sends a message directly to LLM without putting it in the input field first
  const handleQuickSend = useCallback(async (messageText) => {
    if (!messageText?.trim()) return;

    // Auto-switch to chat tab when sending a message
    setActiveContentTab('chat');

    const userMessage = {
      id: Date.now(),
      text: messageText,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError('');

    try {
      const response = await ChatService.sendMessage(
        messageText,
        user?.id,
        currentSession?.sessionId,
        selectedSystem?.id,
        { llmMode }
      );

      applyPreferencesFromResponse(response);

      const responseType = detectResponseType(response);
      const displayText = getResponseText(response);

      const botMessage = {
        id: Date.now() + 1,
        text: displayText || response.response || 'Response received',
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString(),
        responseData: response,
        responseType: responseType,
      };

      setMessages(prev => [...prev, botMessage]);

      // Was missing here (only handleSendMessage's own copy ran this) --
      // fixed 2026-09-28 while wiring the toolbar search bar through this
      // same function, since a search query answered by a client command
      // (e.g. "open the Item workspace filtered to abcd") needs it to fire
      // the same way a typed message's does. See project_toolbar_search.
      runClientCommands(response.clientCommands);
      refreshBackgroundRuns();

      if (response.wizard?.entity && response.wizard?.wizardKey) {
        fetchValidationRules(response.wizard.entity, response.wizard.wizardKey);
      }

      if (response.dashboard) {
        handleDashboardResponse(response.dashboard);
      }

      if (response.taskId) {
        setDismissedTaskIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(response.taskId);
          return newSet;
        });
        subscribeToTask(response.taskId);
      }

      if (!currentSession && response.sessionId) {
        const newSession = { sessionId: response.sessionId, description: 'New conversation', messageCount: 1, lastUpdatedAt: Date.now(), createdAt: Date.now() };
        setSessions(prev => [newSession, ...prev]);
        setCurrentSession(newSession);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      // Was: fabricated a fake bot reply via getRandomSampleResponse() on
      // ANY error, unconditionally, in every environment -- same
      // masking-bug class fixed in handleSendMessage (2026-10-12). Show
      // the real backend message when there is one; a generic, honest
      // unavailability notice otherwise.
      setError(error.isBackendResponse ? error.message : 'Atom is temporarily unavailable. Please try again in a moment.');
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [user?.id, currentSession?.sessionId, selectedSystem?.id, llmMode, runClientCommands]);

  // Fires when a saved prompt is actually used (clicked into input or sent
  // directly) -- fire-and-forget, never blocks the click it's tracking.
  // Hoisted to top level (was previously inline inside the sidebar's now-
  // removed My Prompts section) so PromptsMenuPopover can use it too.
  const recordQuickMenuUse = useCallback((item) => {
    if (item.isUserItem && item.id) {
      ChatService.recordQuickMenuItemUse(item.id);
    }
  }, []);

  // Chat file attachment handlers
  const handleAttachClick = () => {
    chatFileInputRef.current?.click();
  };

  const handleFilesSelected = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      // Add new files to existing attachments
      setAttachedFiles(prev => [...prev, ...files]);
    }
    // Reset input so same file can be selected again
    if (chatFileInputRef.current) {
      chatFileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleClearAttachments = () => {
    setAttachedFiles([]);
  };

  // Get file icon based on type for attachments
  const getAttachmentIcon = (file) => {
    const type = file.type || '';
    const name = file.name || '';
    if (type === 'text/csv' || name.endsWith('.csv')) {
      return <CsvIcon sx={{ fontSize: 20, color: '#22c55e' }} />;
    } else if (type === 'application/pdf' || name.endsWith('.pdf')) {
      return <PdfIcon sx={{ fontSize: 20, color: '#ef4444' }} />;
    } else if (type.startsWith('image/')) {
      return <ImageIcon sx={{ fontSize: 20, color: '#3b82f6' }} />;
    }
    return <FileIcon sx={{ fontSize: 20, color: '#71717a' }} />;
  };

  // Format file size for attachments
  const formatAttachmentSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleNewSession = useCallback(() => {
    setCurrentSession(null);
    setMessages([
      {
        id: 1,
        text: "Hello! I'm your AI assistant. How can I help you today?",
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString(),
      },
    ]);
    setError('');
    // A new chat should also clear the accumulated Atom Response tab (grid/table
    // results stacked from the PREVIOUS conversation) -- otherwise old tables sit
    // there looking like they belong to the fresh conversation. See
    // project_atom_response_workspace_tab.
    setAtomResponseResults([]);
    processedTableMessageIds.current.clear(); // else reopening that chat skips its tables (FH-180)
    setHighlightedResultId(null);
    // Mobile always shows chat directly now -- the old Sessions/Chat tab
    // switcher (and its setMobileView(1) here) was removed along with the
    // Sessions sidebar. See project_two_panel_layout.
  }, []);

  // Helper to find the user message that triggered a bot response
  const findPrecedingUserMessage = (botMessageId) => {
    const botIndex = messages.findIndex(m => m.id === botMessageId);
    if (botIndex > 0) {
      // Look backwards for the most recent user message
      for (let i = botIndex - 1; i >= 0; i--) {
        if (messages[i].sender === 'user') {
          return messages[i].text;
        }
      }
    }
    return null;
  };

  // Feedback handlers
  const handleThumbsUp = async (message) => {
    console.log('Positive feedback for message:', message.id);

    // Send feedback to backend for continuous learning
    const userMessage = findPrecedingUserMessage(message.id);
    const botResponse = message.responseData
      ? getResponseText(message.responseData)
      : message.text;

    await ChatService.sendFeedback({
      messageId: String(message.id),
      sessionId: currentSession?.sessionId,
      type: 'positive',
      userMessage: userMessage,
      botResponse: botResponse,
    });

    setFeedbackSnackbar(true);
  };

  const handleThumbsDown = async (message) => {
    const newFlagged = new Set(flaggedMessages);
    if (newFlagged.has(message.id)) {
      // Unflag - remove the flag (send unflag feedback to backend)
      newFlagged.delete(message.id);
      setFlaggedMessages(newFlagged);

      // Notify backend that user removed the flag
      await ChatService.sendFeedback({
        messageId: String(message.id),
        sessionId: currentSession?.sessionId,
        type: 'unflag',
      });
    } else {
      // Flag - add flag and open feedback dialog
      newFlagged.add(message.id);
      setFlaggedMessages(newFlagged);
      setFeedbackMessage(message);
      setFeedbackText('');
      setFeedbackDialog(true);
    }
  };

  const handleSubmitFeedback = async () => {
    if (feedbackMessage) {
      console.log('Negative feedback for message:', feedbackMessage.id, 'Feedback:', feedbackText);

      // Send detailed negative feedback to backend for continuous learning
      const userMessage = findPrecedingUserMessage(feedbackMessage.id);
      const botResponse = feedbackMessage.responseData
        ? getResponseText(feedbackMessage.responseData)
        : feedbackMessage.text;

      await ChatService.sendFeedback({
        messageId: String(feedbackMessage.id),
        sessionId: currentSession?.sessionId,
        type: 'negative',
        comment: feedbackText,
        userMessage: userMessage,
        botResponse: botResponse,
      });
    }
    setFeedbackDialog(false);
    setFeedbackMessage(null);
    setFeedbackText('');
    setFeedbackSnackbar(true);
  };

  const handleCopyMessage = (message) => {
    const textToCopy = message.responseData
      ? getResponseText(message.responseData)
      : message.text;
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopySnackbar(true);
    });
  };

  // Screenshot message handler
  const handleScreenshotMessage = useCallback(async (messageId) => {
    const messageElement = messageRefs.current[messageId];
    if (!messageElement) {
      console.error('Message element not found');
      return;
    }

    try {
      // Capture the message element as canvas
      const canvas = await html2canvas(messageElement, {
        backgroundColor: colors.surface,
        scale: 2, // Higher resolution
        logging: false,
        useCORS: true,
      });

      // Convert canvas to blob
      canvas.toBlob(async (blob) => {
        if (!blob) {
          console.error('Failed to create blob');
          return;
        }

        try {
          // Copy to clipboard using ClipboardItem
          await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': blob })
          ]);
          setCopySnackbar(true); // Reuse the copy snackbar for feedback
        } catch (clipboardError) {
          // Fallback: download the image if clipboard API fails
          console.warn('Clipboard API failed, downloading instead:', clipboardError);
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `message-screenshot-${messageId}.png`;
          a.click();
          URL.revokeObjectURL(url);
        }
      }, 'image/png');
    } catch (error) {
      console.error('Screenshot failed:', error);
    }
  }, [colors.surface]);

  // Wizard form handlers
  const handleWizardFieldChange = (fieldId, value) => {
    setWizardFormValues(prev => ({
      ...prev,
      [fieldId]: value
    }));

    // Run frontend validation if rules are loaded
    if (validationRules) {
      const result = ValidationService.validateField(fieldId, value, validationRules, 'ON_CHANGE');
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        if (result.errors.length > 0) {
          // Take the first error message for display
          newErrors[fieldId] = result.errors[0].message;
        } else {
          delete newErrors[fieldId];
        }
        return newErrors;
      });
    }
  };

  // Handle field blur - validate field value via generic API
  // The backend determines what validation to run based on entity/field name
  const handleWizardFieldBlur = async (fieldId, value, entityName) => {
    // Skip validation if empty
    if (!value || !value.trim()) {
      return;
    }

    // Skip if no entity name provided (required for validation lookup)
    if (!entityName) {
      console.log('No entityName provided for field validation, skipping');
      return;
    }

    setValidatingFields(prev => ({ ...prev, [fieldId]: true }));
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldId]; // Clear previous error while validating
      return newErrors;
    });

    try {
      const token = localStorage.getItem('waide_access_token');
      const systemId = selectedSystem?.id || '';

      // Call generic field validation endpoint - backend looks up rules from DB
      const response = await fetch(`/api/v1/validation/field${systemId ? `?connectedSystemId=${systemId}` : ''}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          entityName: entityName,
          fieldName: fieldId,
          value: value.trim(),
          triggerOn: 'ON_BLUR'
        })
      });

      const result = await response.json();
      console.log(`Field validation result for ${entityName}.${fieldId}:`, result);

      if (result.valid) {
        // Store validated data for later use (e.g., receiveInventoryId for putaway)
        setValidatedFieldData(prev => ({
          ...prev,
          [fieldId]: {
            value: value.trim(),
            ...result.data, // Include all data returned by validation
            receiveInventoryId: result.receiveInventoryId,
            itemId: result.itemId,
            quantity: result.quantity,
            locationId: result.locationId,
          }
        }));
        // Clear any error
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[fieldId];
          return newErrors;
        });
      } else {
        // Set error
        setFieldErrors(prev => ({
          ...prev,
          [fieldId]: result.error || 'Validation failed'
        }));
        // Clear validated data
        setValidatedFieldData(prev => {
          const newData = { ...prev };
          delete newData[fieldId];
          return newData;
        });
      }
    } catch (error) {
      console.error(`Field validation error for ${fieldId}:`, error);
      setFieldErrors(prev => ({
        ...prev,
        [fieldId]: 'Unable to validate. Please try again.'
      }));
    } finally {
      setValidatingFields(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  // Entity-backed field validation via a FLEX script (e.g. "does this ID
  // already exist" checks) -- reusable across any wizard field that sets a
  // validation_script, not specific to Area. Same call contract as
  // EntityWizardOverlay.js's handleFieldBlur (the workspace-form system's
  // already-proven version of this), reusing the field-error/validating
  // state this chat wizard already has, so errors render identically to
  // the older validation_rules-based blur path.
  const handleWizardScriptValidate = async (fieldId, value, scriptName) => {
    setValidatingFields(prev => ({ ...prev, [fieldId]: true }));
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldId];
      return newErrors;
    });

    try {
      const response = await FlexService.executeScript({
        scriptName,
        context: { _payload: { [fieldId]: value }, fieldValue: value, fieldName: fieldId },
      });

      const scriptSuccess = response.context?.success;
      if (!response.success || scriptSuccess === false) {
        const msg = response.context?.error_message || response.errorMessage || 'Invalid value';
        setFieldErrors(prev => ({ ...prev, [fieldId]: msg }));
      } else {
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[fieldId];
          return newErrors;
        });
      }
    } catch (error) {
      console.error(`Script validation error for ${fieldId}:`, error);
      setFieldErrors(prev => ({
        ...prev,
        [fieldId]: 'Unable to validate. Please try again.'
      }));
    } finally {
      setValidatingFields(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  // Fetch validation rules when wizard is displayed
  const fetchValidationRules = useCallback(async (entityName, wizardKey) => {
    if (!entityName) return;

    try {
      const rules = await ValidationService.fetchRulesForWizard(entityName, wizardKey);
      setValidationRules(rules);
      setFieldErrors({}); // Clear previous errors
    } catch (error) {
      console.error('Failed to fetch validation rules:', error);
    }
  }, []);

  // Clear validation state when wizard is completed or cancelled
  const clearValidationState = useCallback(() => {
    setValidationRules(null);
    setFieldErrors({});
    setValidatingFields({});
    setValidatedFieldData({});
  }, []);

  const handleWizardAction = async (action, message) => {
    if (action.type === 'CANCEL') {
      // Show confirmation dialog before canceling
      const confirmed = window.confirm('Are you sure you want to cancel this wizard? All entered data will be lost.');
      if (!confirmed) {
        return;
      }

      // Send cancel action to backend to archive/abandon the wizard
      try {
        const sessionId = currentSession?.sessionId || message?.responseData?.sessionId;
        const response = await ChatService.submitWizardForm({
          userId: user?.id,
          sessionId: sessionId,
          connectedSystemId: selectedSystem?.id,
          action: 'cancel',
          fieldValues: {}
        });

        // Clear form values, validation state, and mark wizard as submitted
        setWizardFormValues({});
        clearValidationState();
        setSubmittedWizards(prev => new Set([...prev, message.id]));

        // Cancelling starts a fresh chat session -- the aborted wizard
        // conversation shouldn't linger as context for whatever the user
        // asks Atom next. Cancellation confirmation becomes the new
        // session's first message.
        const cancelMessage = response?.response || 'Wizard cancelled. How else can I help you?';
        handleNewSession();
        setMessages(prev => [
          ...prev,
          {
            id: Date.now(),
            text: cancelMessage,
            sender: 'bot',
            timestamp: new Date().toLocaleTimeString(),
            responseData: response
          }
        ]);
      } catch (err) {
        console.error('Failed to cancel wizard:', err);
        // Still clear local state even if backend call fails
        setWizardFormValues({});
        clearValidationState();
        handleNewSession();
        setMessages(prev => [
          ...prev,
          {
            id: Date.now(),
            text: 'Wizard cancelled.',
            sender: 'bot',
            timestamp: new Date().toLocaleTimeString(),
          }
        ]);
      }
      return;
    }

    // Check if this is the connected system selector
    if (message.responseData?.wizard?.wizardKey === 'SELECT_SYSTEM') {
      // Logout escape hatch: a multi-system user isn't forced through this
      // gate to reach the rest of the app if they'd rather sign out instead
      // (e.g. wrong account, or every system they see is one they no longer
      // want to work in).
      if (action.actionId === 'logout') {
        logout();
        return;
      }

      const connectedSystemId = wizardFormValues['connected_system_id'];
      const selected = user?.connectedSystems?.find(wh => wh.id === connectedSystemId);

      if (selected) {
        // Optimistic update - save selection to context immediately
        selectSystem(selected);
        // Open workspaces stay open (no close/keep prompt) -- just reload
        // seamlessly under the newly selected system.
        workspacePanelRef.current?.refreshAllTabs();

        // Mark wizard as submitted
        setSubmittedWizards(prev => new Set([...prev, message.id]));
        setWizardFormValues({});

        // Send system selection via chat API to get preferences
        try {
          const response = await ChatService.sendMessage(
            `select system ${selected.id}`,
            user?.id,
            currentSession?.sessionId,
            selected.id
          );

          // Extract and apply preferences from response
          if (response) {
            applyPreferencesFromResponse(response);
          }
        } catch (err) {
          console.warn('Failed to fetch preferences on system selection:', err);
          // Selection still succeeded - preferences will use defaults
        }

        // Add confirmation messages
        setMessages(prev => [
          ...prev,
          {
            id: Date.now(),
            text: `Selected: ${selected.systemName}`,
            sender: 'user',
            timestamp: new Date().toLocaleTimeString(),
          },
          {
            id: Date.now() + 1,
            text: `Great! You're now working with **${selected.systemName}** (${selected.systemCode}). How can I help you today?`,
            sender: 'bot',
            timestamp: new Date().toLocaleTimeString(),
          }
        ]);
      }
      return;
    }

    // Check if this is the system color change wizard
    if (message.responseData?.wizard?.wizardKey === 'CHANGE_SYSTEM_COLOR') {
      const newColor = wizardFormValues['color'];
      if (newColor && selectedSystem) {
        // Update local state immediately for responsive UI
        setSystemColors(prev => ({
          ...prev,
          [selectedSystem.id]: newColor
        }));
      }
      // Continue with normal wizard submission to persist to backend
    }

    // Block submission if there are validation errors
    if (Object.keys(fieldErrors).length > 0) {
      // Show error message to user
      const errorFields = Object.keys(fieldErrors).join(', ');
      console.warn('Validation errors prevent submission:', fieldErrors);
      // Optionally show a snackbar or alert
      alert(`Please fix validation errors before proceeding: ${errorFields}`);
      return;
    }

    // Handle Submit, Next, or any action that should send data
    // Get the form fields from the message (required and optional)
    const formFields = message?.responseData?.wizard?.form?.fields || [];
    const optionalFields = message?.responseData?.wizard?.form?.optionalFields || [];
    const assumptions = message?.responseData?.wizard?.assumptions || [];
    const allFormFields = [...formFields, ...optionalFields];

    // Build field values object from form state
    const fieldValues = {};

    allFormFields.forEach(field => {
      const value = wizardFormValues[field.fieldId] !== undefined
        ? wizardFormValues[field.fieldId]
        : (field.value || field.defaultValue);
      if (value !== undefined && value !== null && value !== '') {
        // A File value has no way to travel in the JSON wizard submission
        if (!(value instanceof File)) {
          fieldValues[field.fieldId] = value;
        }
      }
    });

    // Include any edited assumption values (e.g., receiving_location)
    assumptions.forEach(assumption => {
      const editedValue = wizardFormValues[assumption.fieldId];
      if (editedValue !== undefined && editedValue !== null && editedValue !== '') {
        fieldValues[assumption.fieldId] = editedValue;
      } else if (assumption.assumedValue !== undefined && assumption.assumedValue !== null) {
        // Include the original assumption value if not edited
        fieldValues[assumption.fieldId] = assumption.assumedValue;
      }
    });

    // Build user-friendly message for display
    const fieldStrings = formFields
      .map(field => {
        const value = fieldValues[field.fieldId];
        // For file fields, show the filename
        if (field.type === 'FILE' && wizardFormValues[field.fieldId] instanceof File) {
          return `${field.label}: ${wizardFormValues[field.fieldId].name}`;
        }
        return value ? `${field.label}: ${value}` : null;
      })
      .filter(Boolean);

    const messageText = action.actionId === 'add_line'
      ? `Adding line: ${fieldStrings.join(', ')}`
      : action.actionId === 'add_more'
      ? `Adding item: ${fieldStrings.join(', ')}`
      : action.actionId === 'edit_line'
      ? `Editing line: ${fieldStrings.join(', ')}`
      : action.actionId === 'remove_line'
      ? 'Removing line'
      : action.actionId === 'done_adding'
      ? 'Done adding lines'
      : fieldStrings.join(', ');

    // Add user message to chat
    const userMessage = {
      id: Date.now(),
      text: messageText || action.label,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString(),
    };
    setMessages(prev => [...prev, userMessage]);

    // Send to backend via wizard endpoint
    setIsLoading(true);
    setError('');

    try {
      // Use sessionId from currentSession, or fallback to the sessionId from the message's responseData
      const sessionId = currentSession?.sessionId || message?.responseData?.sessionId;

      if (!sessionId) {
        console.error('No sessionId available for wizard form submission', { currentSession, messageResponseData: message?.responseData });
      }

      let response;

      response = await ChatService.submitWizardForm({
        userId: user?.id,
        sessionId: sessionId,
        connectedSystemId: selectedSystem?.id,
        action: action.actionId,
        fieldValues
      });

      // Extract preferences if present in response
      applyPreferencesFromResponse(response);

      // Check if wizard is completed
      const wizardCompleted = response.wizard?.status === 'COMPLETED';

      // Only mark as submitted if wizard is completed or moving to next step
      if (wizardCompleted || action.actionId === 'done_adding' || action.actionId === 'submit') {
        setSubmittedWizards(prev => new Set([...prev, message.id]));
      }

      // Clear form values for actions that add/edit lines (to reset for next entry)
      if (action.actionId === 'add_line' || action.actionId === 'edit_line' || action.actionId === 'add_more' || action.actionId === 'add_another') {
        setWizardFormValues({});
        setFieldErrors({}); // Clear validation errors for next entry
      } else if (wizardCompleted) {
        setWizardFormValues({});
        clearValidationState(); // Clear validation state when wizard completes
      }

      const botMessage = {
        id: Date.now() + 1,
        text: response.response,
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString(),
        responseData: response,
      };
      setMessages(prev => [...prev, botMessage]);

      // Fetch validation rules if a new wizard step is present
      if (response.wizard?.entity && response.wizard?.wizardKey && !wizardCompleted) {
        fetchValidationRules(response.wizard.entity, response.wizard.wizardKey);
      }

      // Ensure currentSession is set with sessionId from response or the one we used
      const effectiveSessionId = response.sessionId || sessionId;
      if (effectiveSessionId && !currentSession) {
        const newSession = { sessionId: effectiveSessionId, description: 'New conversation', messageCount: 1, lastUpdatedAt: Date.now(), createdAt: Date.now() };
        setCurrentSession(newSession);
        setSessions(prev => [newSession, ...prev]);
      }
    } catch (err) {
      console.error('Error sending wizard data:', err);
      // Show error to user
      const errorMessage = {
        id: Date.now() + 1,
        text: `Error: ${err.message || 'Failed to submit wizard form'}`,
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString(),
        isError: true,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  // Handle action button clicks - send directly to backend
  const handleActionClick = async (sentence, action) => {
    console.log('handleActionClick called with:', sentence);
    // A button with mode "prefill" puts its text in the input box so the user can edit it and send it
    // (Enter or the send button), instead of sending it straight away.
    if (action && action.mode === 'prefill' && sentence && sentence.trim()) {
      setInputMessage(sentence);
      setTimeout(() => inputRef.current?.focus(), 100);
      return;
    }
    if (!sentence || !sentence.trim()) {
      // Blank-sentence action (e.g. an onboarding "Something else" choice) —
      // don't send anything, just put the cursor in the input box so the
      // user can type their own free-text answer.
      setInputMessage('');
      setTimeout(() => inputRef.current?.focus(), 100);
      return;
    }

    // Client-side workspace navigation, bypassing chat/LLM entirely -- e.g.
    // the "Start Exploring" button after facility_setup_wizard completes.
    // Format: __OPEN_WORKSPACE__:<code>:<title>
    // Also starts a fresh chat session -- this fires from a wizard's
    // post-completion action (e.g. "Start Exploring"), and the just-finished
    // wizard conversation shouldn't linger as context for whatever the user
    // asks Atom next.
    if (sentence.startsWith('__OPEN_WORKSPACE__:')) {
      const [, workspaceCode, workspaceTitle] = sentence.split(':');
      handleOpenWorkspace(workspaceCode, workspaceTitle || workspaceCode);
      handleNewSession();
      return;
    }

    // Same client-side workspace navigation, but WITHOUT resetting the chat
    // session. Real bug fixed 2026-09-22: mid-conversation agent answers
    // (e.g. "here's how receiving works" -> "Open Receiving workspace"
    // button) started reusing __OPEN_WORKSPACE__ and inherited its session
    // reset, which was only ever correct for the one-off post-wizard
    // "Start Exploring" case -- clicking it wiped out the ongoing Q&A the
    // button was attached to. Format: __OPEN_WORKSPACE_KEEP_CHAT__:<code>:<title>
    if (sentence.startsWith('__OPEN_WORKSPACE_KEEP_CHAT__:')) {
      const [, workspaceCode, workspaceTitle] = sentence.split(':');
      handleOpenWorkspace(workspaceCode, workspaceTitle || workspaceCode);
      return;
    }

    // Check for direct tool routing from agent panel buttons
    if (sentence.startsWith('__DIRECT_TOOL__:')) {
      const toolKey = sentence.replace('__DIRECT_TOOL__:', '');
      const displayName = toolKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

      const userMessage = {
        id: Date.now(),
        text: `Start ${displayName}`,
        sender: 'user',
        timestamp: new Date().toLocaleTimeString(),
      };
      setMessages(prev => [...prev, userMessage]);
      setIsLoading(true);
      setError('');

      try {
        const response = await ChatService.sendMessage(
          `Start ${toolKey}`, user?.id, currentSession?.sessionId, selectedSystem?.id,
          { llmMode: 'DIRECT_TOOL', toolKey }
        );

        const botMessage = {
          id: Date.now() + 1,
          text: response.response,
          sender: 'bot',
          timestamp: new Date().toLocaleTimeString(),
          responseData: response,
        };
        setMessages(prev => [...prev, botMessage]);

        if (response.sessionId && !currentSession) {
          const newSession = { sessionId: response.sessionId, description: 'New conversation', messageCount: 1, lastUpdatedAt: Date.now(), createdAt: Date.now() };
          setCurrentSession(newSession);
          setSessions(prev => [newSession, ...prev]);
        }
      } catch (err) {
        console.error('Error starting tool:', err);
        // Was: fabricated a fake bot reply via getRandomSampleResponse() on
        // ANY error, unconditionally, in every environment -- same
        // masking-bug class fixed in handleSendMessage (2026-10-12). Show
        // the real backend message when there is one; a generic, honest
        // unavailability notice otherwise.
        setError(err.isBackendResponse ? err.message : 'Atom is temporarily unavailable. Please try again in a moment.');
      } finally {
        setIsLoading(false);
        setTimeout(() => inputRef.current?.focus(), 100);
      }
      return;
    }

    // Add user message to chat
    const userMessage = {
      id: Date.now(),
      text: sentence,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString(),
    };
    setMessages(prev => [...prev, userMessage]);

    // Send to backend
    setIsLoading(true);
    setError('');

    try {
      const response = await ChatService.sendMessage(sentence, user?.id, currentSession?.sessionId, selectedSystem?.id);

      const botMessage = {
        id: Date.now() + 1,
        text: response.response,
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString(),
        responseData: response,
      };
      setMessages(prev => [...prev, botMessage]);
      runClientCommands(response.clientCommands);
      refreshBackgroundRuns();

      if (response.sessionId && !currentSession) {
        const newSession = { sessionId: response.sessionId, description: 'New conversation', messageCount: 1, lastUpdatedAt: Date.now(), createdAt: Date.now() };
        setCurrentSession(newSession);
        setSessions(prev => [newSession, ...prev]);
      }
    } catch (err) {
      console.error('Error sending action:', err);
      // Was: fabricated a fake bot reply via getRandomSampleResponse() on
      // ANY error, unconditionally, in every environment -- same
      // masking-bug class fixed in handleSendMessage (2026-10-12). Show
      // the real backend message when there is one; a generic, honest
      // unavailability notice otherwise.
      setError(err.isBackendResponse ? err.message : 'Atom is temporarily unavailable. Please try again in a moment.');
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  // FH-419: a screen's ASK_ATOM action (Learning > Start) opens the chat and sends its sentence, as if typed
  const handleActionClickRef = useRef(handleActionClick);
  handleActionClickRef.current = handleActionClick;
  useEffect(() => {
    const onAsk = (e) => {
      const sentence = e?.detail?.sentence;
      if (!sentence) return;
      setChatCollapsed(false);
      handleActionClickRef.current(sentence);
    };
    window.addEventListener('atom:ask', onAsk);
    return () => window.removeEventListener('atom:ask', onAsk);
  }, []);

  // Table action handlers
  const handleTableRowAction = async (action, row, sentence) => {
    console.log('Table row action:', action.actionId, row.rowId, sentence);
    // Send the action sentence to backend
    if (sentence) {
      await handleActionClick(sentence);
    }
  };

  const handleTableBulkAction = async (action, selectedIds, sentence) => {
    console.log('Table bulk action:', action.actionId, selectedIds, sentence);
    // Send the action sentence to backend
    if (sentence) {
      await handleActionClick(sentence);
    }
  };

  const handleTableGlobalAction = async (action, sentence) => {
    console.log('Table global action:', action.actionId, sentence);
    // Send the action sentence to backend
    if (sentence) {
      await handleActionClick(sentence);
    }
  };

  const handleTableLinkClick = (columnId, value, rowData) => {
    console.log('Table link clicked:', columnId, value, rowData);
    // For link clicks, create a view details query
    const sentence = `Show me details of ${value}`;
    handleActionClick(sentence);
  };

  const handleTableConfigure = (tableId, entityType) => {
    if (!entityType) {
      console.warn('Table configure: no entityType on table, cannot load column metadata for', tableId);
      return;
    }
    setColumnConfigDialog({ open: true, tableId, entityType });
  };

  const handleTableFilterRemove = (filter) => {
    console.log('Filter removed:', filter);
    // Send refresh query without the removed filter
    const sentence = `remove filter ${filter.filterId} and refresh`;
    handleActionClick(sentence);
  };

  const handleTableAddFilter = (filterData) => {
    console.log('Filter added:', filterData);
    // Send query to apply the new filter
    const sentence = `filter by ${filterData.columnId} ${filterData.operator} ${Array.isArray(filterData.value) ? filterData.value.join(' and ') : filterData.value}`;
    handleActionClick(sentence);
  };

  // Timeline handlers
  const handleRelatedEntityClick = (entity) => {
    console.log('Related entity clicked:', entity);
    // Navigate to the related entity by sending a query
    const sentence = `Show me details of ${entity.type} ${entity.id}`;
    handleActionClick(sentence);
  };

  // Dashboard handlers

  // Persist open dashboards to localStorage
  useEffect(() => {
    DashboardService.saveOpenDashboards(openDashboards);
  }, [openDashboards]);

  // Load saved dashboards list for pin dialog
  const loadSavedDashboards = async () => {
    try {
      const dashboards = await DashboardService.listDashboards();
      setSavedDashboards(dashboards);
    } catch (error) {
      // FH-97 FIXED 2026-10-12: this used to silently populate the Pin to
      // Dashboard picker with 3 fabricated dashboards (dash-001/002/003) --
      // the actual upstream source of the fake IDs handleConfirmPin was
      // consuming before FH-94 fixed that function's own side of it. An
      // empty list here is honest (DashboardService.listDashboards already
      // falls back to a real last-known-good cache first, if one exists --
      // this only runs when even that isn't available).
      console.error('Could not load dashboards from backend:', error);
      setSavedDashboards([]);
      setError(error.isBackendResponse ? error.message : 'Could not load your dashboards. Please try again.');
    }
  };

  // Open a dashboard as a tab
  const handleOpenDashboard = (dashboard) => {
    if (!openDashboards.find(d => d.dashboardId === dashboard.dashboardId)) {
      setOpenDashboards(prev => [...prev, dashboard]);
    }
    setActiveContentTab(dashboard.dashboardId);
  };

  // Close a dashboard tab
  const handleCloseDashboard = (dashboardId) => {
    setOpenDashboards(prev => prev.filter(d => d.dashboardId !== dashboardId));
    if (activeContentTab === dashboardId) {
      setActiveContentTab('chat');
    }
  };

  // Handle content tab change
  const handleContentTabChange = (newTab) => {
    setActiveContentTab(newTab);
  };

  // Pin action - opens dialog to select dashboard
  const handlePinToBoard = (type, data, sourceQuery) => {
    setPinDialogData({ type, data, sourceQuery });
    setPinDialogLoading(true);
    setPinDialogOpen(true);
    loadSavedDashboards().finally(() => setPinDialogLoading(false));
  };

  // Confirm pin to specific dashboard
  const handleConfirmPin = async (dashboardId, isNewDashboard, newDashboardName) => {
    try {
      let targetDashboardId = dashboardId;
      let targetDashboard = null;

      if (isNewDashboard && newDashboardName) {
        // Create new dashboard
        // FH-95 FIXED 2026-10-12: this used to fabricate a client-side-only
        // dashboard ID and proceed as if creation succeeded whenever the
        // real call failed -- the user saw "Pinned to [name]" and a
        // dashboard that rendered for the rest of the session, but nothing
        // was ever saved server-side (gone on refresh, no warning). Let a
        // real failure propagate to this function's own outer catch.
        const newDashboard = await DashboardService.createDashboard(newDashboardName);
        targetDashboardId = newDashboard.dashboardId;
        targetDashboard = newDashboard;
      } else {
        // Find existing dashboard
        targetDashboard = openDashboards.find(d => d.dashboardId === targetDashboardId);
        if (!targetDashboard) {
          // FH-94 FIXED 2026-10-12: this used to silently substitute a fake
          // sample dashboard (sampleDashboardDailyOps/Shipping, or an empty
          // stub) whenever the real fetch failed, with only a console.log --
          // no NODE_ENV gate at all, and no way for the user to know the
          // "Daily Ops"/"Shipping" dashboard they were pinning to wasn't
          // their real data. A failure here genuinely means the pin cannot
          // proceed correctly (we'd be adding a panel to a fabricated
          // dashboard object, not the real one), so let it propagate to this
          // function's own outer catch, which already shows a real error
          // (see below) instead of silently faking success.
          targetDashboard = await DashboardService.getDashboard(targetDashboardId);
        }
      }

      // Create new panel from pinned data
      const newPanel = {
        panelId: `panel-${Date.now()}`,
        type: pinDialogData.type,
        title: pinDialogData.data.table?.title || pinDialogData.data.chart?.title || 'Pinned Panel',
        sourceQuery: pinDialogData.sourceQuery,
        refreshConfig: { mode: 'MANUAL' },
        data: pinDialogData.data,
        position: { row: Math.floor((targetDashboard.panels?.length || 0) / 2), col: (targetDashboard.panels?.length || 0) % 2 },
      };

      // FH-95 FIXED 2026-10-12: this used to swallow a real addPanel
      // failure entirely (console.log only, no setError, didn't even reach
      // this function's own outer catch) and proceed straight to the
      // success snackbar regardless of whether the panel was actually
      // persisted. Let a real failure propagate instead.
      await DashboardService.addPanel(targetDashboardId, newPanel);

      // Update local state
      const updatedDashboard = {
        ...targetDashboard,
        panels: [...(targetDashboard.panels || []), newPanel],
      };

      // Update open dashboards if already open
      setOpenDashboards(prev => {
        const existingIndex = prev.findIndex(d => d.dashboardId === targetDashboardId);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = updatedDashboard;
          return updated;
        }
        return prev;
      });

      setDashboardSnackbar({ open: true, message: `Pinned to ${updatedDashboard.title}` });
    } catch (error) {
      console.error('Error pinning to dashboard:', error);
      setError(error.isBackendResponse ? error.message : 'Failed to pin to dashboard');
    } finally {
      setPinDialogOpen(false);
      setPinDialogData(null);
    }
  };

  // Save dashboard
  const handleSaveDashboard = async (dashboard) => {
    try {
      await DashboardService.saveDashboard(dashboard.dashboardId, dashboard);
      setDashboardSnackbar({ open: true, message: 'Dashboard saved' });
    } catch (error) {
      // FH-97 FIXED 2026-10-12: this used to show "Dashboard saved locally"
      // as a SUCCESS snackbar even when the real save failed.
      console.error('Error saving dashboard:', error);
      setError(error.isBackendResponse ? error.message : 'Failed to save dashboard. Please try again.');
    }
  };

  // Refresh all panels in a dashboard
  const handleRefreshAllPanels = async (dashboardId) => {
    try {
      const refreshedDashboard = await DashboardService.refreshAllPanels(dashboardId);
      setOpenDashboards(prev => prev.map(d =>
        d.dashboardId === dashboardId ? refreshedDashboard : d
      ));
    } catch (error) {
      // FH-97 FIXED 2026-10-12: this used to fake a refresh by just bumping
      // every panel's lastRefreshed timestamp with no new data on a real
      // failure -- the user saw a fresh timestamp over stale data.
      console.error('Error refreshing panels:', error);
      setError(error.isBackendResponse ? error.message : 'Failed to refresh dashboard. Please try again.');
    }
  };

  // Refresh a single panel
  const handleRefreshPanel = async (dashboardId, panelId) => {
    try {
      const refreshedPanel = await DashboardService.refreshPanel(dashboardId, panelId);
      setOpenDashboards(prev => prev.map(d => {
        if (d.dashboardId === dashboardId) {
          return {
            ...d,
            panels: d.panels.map(p => p.panelId === panelId ? refreshedPanel : p),
          };
        }
        return d;
      }));
    } catch (error) {
      // FH-97 FIXED 2026-10-12: same as handleRefreshAllPanels -- used to
      // fake a refresh by bumping just this panel's lastRefreshed timestamp
      // with no new data on a real failure.
      console.error('Error refreshing panel:', error);
      setError(error.isBackendResponse ? error.message : 'Failed to refresh panel. Please try again.');
    }
  };

  // Remove panel from dashboard
  const handleRemovePanel = async (dashboardId, panelId) => {
    try {
      await DashboardService.removePanel(dashboardId, panelId);
    } catch (error) {
      // FH-97 FIXED 2026-10-12: this used to swallow the error entirely
      // (console.log only) and fall through to updating local UI + showing
      // a success snackbar regardless -- the panel reappeared on reload
      // with zero warning it was never actually removed. Bail out instead;
      // local state stays consistent with the backend.
      console.error('Error removing panel:', error);
      setError(error.isBackendResponse ? error.message : 'Failed to remove panel. Please try again.');
      return;
    }

    setOpenDashboards(prev => prev.map(d => {
      if (d.dashboardId === dashboardId) {
        return {
          ...d,
          panels: d.panels.filter(p => p.panelId !== panelId),
        };
      }
      return d;
    }));
    setDashboardSnackbar({ open: true, message: 'Panel removed from dashboard' });
  };

  // Handle DASHBOARD response type - open dashboard as tab
  const handleDashboardResponse = (dashboard) => {
    handleOpenDashboard(dashboard);
  };

  // Drive/File operation handlers
  const loadDriveData = async () => {
    try {
      // Fetch user's files from the backend
      const response = await DocumentService.listDocuments();
      console.log('listDocuments response:', JSON.stringify(response, null, 2));

      if (response && response.files && response.files.length > 0) {
        console.log('Files found:', response.files.length);
        // Transform the backend response to drive structure
        const driveStructure = transformS3DataToDriveStructure(response.files, {
          title: 'My Files',
          usageInfo: response.usage ? {
            used: response.usage.used || '0 B',
            total: response.usage.total || '5 GB',
            percentage: response.usage.percentage || 0,
          } : undefined,
        });
        console.log('Drive structure:', JSON.stringify(driveStructure, null, 2));
        setDriveData(driveStructure);
      } else {
        console.log('No files found in response:', response);
        // No files found - show empty drive structure
        const emptyDrive = transformS3DataToDriveStructure([], {
          title: 'My Files',
        });
        setDriveData(emptyDrive);
      }
    } catch (error) {
      console.error('Failed to load drive data:', error);
      // Fallback to empty structure on error
      const emptyDrive = transformS3DataToDriveStructure([], {
        title: 'My Files',
      });
      setDriveData(emptyDrive);
    }
  };

  // Lazy load folder contents when user expands/clicks a folder
  const handleLoadFolder = async (folderId, folderPath) => {
    if (!driveData) return;

    // Check if already loaded or loading
    const folder = driveData.items.find(item => item.id === folderId);
    if (!folder || folder.isLoaded || folder.isLoading) return;

    // Set loading state
    setDriveData(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.id === folderId ? { ...item, isLoading: true } : item
      ),
    }));

    try {
      // When backend is available:
      // const contents = await ChatService.getFolderContents(folderPath);

      // For now, use sample folder contents
      const contents = sampleFolderContents[folderPath];

      if (contents) {
        // Transform folder contents into items
        const newItems = transformFolderContents(contents, folderId, folderPath);

        // Get IDs of new children
        const childIds = newItems.map(item => item.id);

        // Update drive data with new items and mark folder as loaded
        setDriveData(prev => ({
          ...prev,
          items: [
            ...prev.items.map(item =>
              item.id === folderId
                ? { ...item, isLoaded: true, isLoading: false, children: childIds }
                : item
            ),
            ...newItems,
          ],
        }));

        console.log(`Loaded folder: ${folderPath}`, newItems.length, 'items');
      } else {
        // No contents found - mark as loaded with empty children
        setDriveData(prev => ({
          ...prev,
          items: prev.items.map(item =>
            item.id === folderId
              ? { ...item, isLoaded: true, isLoading: false, hasChildren: false, children: [] }
              : item
          ),
        }));
        console.log(`Folder empty or not found: ${folderPath}`);
      }
    } catch (error) {
      console.error('Failed to load folder contents:', error);
      // Reset loading state on error
      setDriveData(prev => ({
        ...prev,
        items: prev.items.map(item =>
          item.id === folderId ? { ...item, isLoading: false } : item
        ),
      }));
    }
  };

  const loadOrganizationUsers = async () => {
    try {
      // When backend is available:
      // const users = await ChatService.getOrganizationUsers();
      // setOrganizationUsers(users);

      // For now, use sample data
      setOrganizationUsers([
        { id: 'user-001', username: 'jsmith', displayName: 'John Smith', email: 'john.smith@company.com' },
        { id: 'user-002', username: 'mgarcia', displayName: 'Maria Garcia', email: 'maria.garcia@company.com' },
        { id: 'user-003', username: 'rwilson', displayName: 'Robert Wilson', email: 'robert.wilson@company.com' },
        { id: 'user-004', username: 'sjohnson', displayName: 'Sarah Johnson', email: 'sarah.johnson@company.com' },
        { id: 'user-005', username: 'dlee', displayName: 'David Lee', email: 'david.lee@company.com' },
      ]);
    } catch (error) {
      console.error('Failed to load organization users:', error);
      setOrganizationUsers([]);
    }
  };

  const handleOpenDrive = () => {
    loadDriveData();
    loadOrganizationUsers();
    setDriveDialogOpen(true);
    setAddMenuAnchor(null);
  };

  // Trigger document upload wizard via chat
  const handleShare = async (item, targetUsers) => {
    if (!driveData || !targetUsers || targetUsers.length === 0) return;

    // Helper function to recursively collect all file IDs within a folder
    const collectFileIds = (folderId, items) => {
      const fileIds = [];
      const folderContents = items.filter(i => i.parentId === folderId);

      folderContents.forEach(child => {
        if (child.type === 'FILE') {
          fileIds.push(child.id); // child.id is the s3Key for files
        } else if (child.type === 'FOLDER') {
          // Recursively collect from subfolders
          fileIds.push(...collectFileIds(child.id, items));
        }
      });

      return fileIds;
    };

    // Determine the S3 path and file IDs for the item
    let itemPath;
    let fileIds = [];

    if (item.type === 'FOLDER') {
      // For folders: use folderPath and collect all file IDs within
      itemPath = item.folderPath || '';
      fileIds = collectFileIds(item.id, driveData.items);
    } else {
      // For files: use parent path + filename, and the single s3Key
      const parentFolder = driveData.items.find(i => i.id === item.parentId);
      const parentPath = parentFolder?.folderPath || '';
      itemPath = parentPath ? `${parentPath}/${item.name}` : item.name;
      fileIds = [item.id]; // item.id is the s3Key for files
    }

    // Extract user IDs from the target users array
    const userIds = targetUsers.map(u => u.id);
    const userNames = targetUsers.map(u => u.displayName || u.username).join(', ');

    console.log('Share:', item.name, 'at path:', itemPath);
    console.log('File IDs (s3Keys):', fileIds);
    console.log('With users:', userIds);

    try {
      // POST /api/drive/share
      // await ChatService.shareItem({
      //   path: itemPath,
      //   fileIds: fileIds,
      //   userIds: userIds,
      //   accessLevel: 'VIEW'
      // });

      // For now, just log the API call that would be made
      console.log('API call: POST /api/drive/share', {
        path: itemPath,
        fileIds: fileIds,           // Array of s3Keys
        userIds: userIds,           // Array of user IDs
        accessLevel: 'VIEW',
        itemType: item.type,
        itemName: item.name,
      });

      // Show success feedback (could add a snackbar here)
      console.log(`Successfully shared "${item.name}" (${fileIds.length} file(s)) with ${userNames}`);
    } catch (error) {
      console.error('Failed to share item:', error);
    }
  };

  // fileEntries: [{ file, relativeDir }] -- relativeDir is the subfolder path (if any) the
  // file was picked from, relative to the folder picker's root, e.g. "policies/2026" for a
  // folder upload. Empty for a plain multi-file select. Uploads sequentially (not in
  // parallel) so a large folder doesn't hammer the API all at once, and posts a single
  // aggregate chat message at the end rather than one per file.
  const handleFileUpload = async (fileEntries, folderId, onProgress) => {
    if (!driveData || !fileEntries || fileEntries.length === 0) return;

    const folder = driveData.items.find(item => item.id === folderId);
    const baseFolderPath = folder?.folderPath || '';

    const succeeded = [];
    const failed = [];
    const newItems = [];

    for (let i = 0; i < fileEntries.length; i++) {
      const { file, relativeDir } = fileEntries[i];
      const uploadPath = [baseFolderPath, relativeDir].filter(Boolean).join('/');

      console.log('Uploading:', file.name, 'to path:', uploadPath);

      try {
        const result = await DocumentService.uploadDocument(file, uploadPath, selectedSystem?.id);
        succeeded.push({ file, uploadPath, result });

        if (result.documentId) {
          newItems.push({
            id: result.documentId,
            name: file.name,
            type: 'FILE',
            mimeType: file.type || 'application/octet-stream',
            size: file.size,
            parentId: folderId,
            folderPath: uploadPath,
            s3Key: result.s3Key,
            modifiedAt: new Date().toISOString(),
            createdAt: new Date().toISOString(),
          });
        }
      } catch (error) {
        console.error('Failed to upload file:', file.name, error);
        failed.push({ file, error });
      }

      if (onProgress) onProgress(i + 1, fileEntries.length);
    }

    if (newItems.length > 0) {
      setDriveData(prev => ({
        ...prev,
        items: [...prev.items, ...newItems],
      }));
    }

    // Single-file case keeps the original, more detailed message; a batch (multi-file or
    // folder) gets an aggregate summary instead of one message per file.
    let text;
    if (fileEntries.length === 1) {
      if (succeeded.length === 1) {
        const { file, uploadPath, result } = succeeded[0];
        text = `Successfully uploaded **${file.name}** to the knowledge base.\n\n` +
               `- Document ID: \`${result.documentId}\`\n` +
               `- Chunks created: ${result.chunksCreated || 'N/A'}\n` +
               `- Path: ${uploadPath || 'root'}`;
      } else {
        text = `Failed to upload **${failed[0].file.name}**: ${failed[0].error.message}`;
      }
    } else {
      const lines = [`Uploaded **${succeeded.length} of ${fileEntries.length}** files to the knowledge base.`];
      if (failed.length > 0) {
        lines.push('', `**Failed (${failed.length}):**`);
        failed.forEach(f => lines.push(`- ${f.file.name}: ${f.error.message}`));
      }
      text = lines.join('\n');
    }

    setMessages(prev => [...prev, {
      id: `upload-result-${Date.now()}`,
      type: 'bot',
      timestamp: new Date().toISOString(),
      text,
    }]);
  };

  const handleFileDelete = async (itemId) => {
    console.log('Delete:', itemId);

    if (!driveData) return;

    // Find the item to get its docId
    const item = driveData.items.find(i => i.id === itemId);

    // If it's a file, delete from backend
    if (item && item.type === 'FILE') {
      try {
        // The item should have a docId from the backend response
        const docId = item.docId || itemId;
        await DocumentService.deleteDocument(docId);
        console.log('Successfully deleted from backend:', docId);
      } catch (error) {
        console.error('Failed to delete from backend:', error);
        // Still remove from local state for better UX
      }
    }

    // Update local state to remove the item
    setDriveData({
      ...driveData,
      items: driveData.items.filter(i => i.id !== itemId),
    });
  };

  const handleFileRename = async (itemId, newName) => {
    // PUT /api/drive/items/{itemId}
    console.log('Rename:', itemId, 'to', newName);
    // TODO: Implement actual rename when backend is available
    // For now, update local state
    if (driveData) {
      setDriveData({
        ...driveData,
        items: driveData.items.map(item =>
          item.id === itemId ? { ...item, name: newName } : item
        ),
      });
    }
  };

  const handleFileDownload = async (item) => {
    console.log('Download:', item.name, 'docId:', item.docId);

    if (!item.docId) {
      console.error('No docId available for download');
      return;
    }

    try {
      // Get presigned download URL via authenticated API call
      const downloadUrl = await DocumentService.getDocumentDownloadUrl(item.docId);
      if (downloadUrl) {
        window.open(downloadUrl, '_blank');
      }
    } catch (error) {
      console.error('Failed to get download URL:', error);
      // Could show a snackbar/toast here to inform user
    }
  };

  const handleDocumentSearch = async (query) => {
    console.log('Searching documents:', query);
    const results = await DocumentService.searchDocuments(query);
    return results;
  };

  const handleFilePreview = async (file) => {
    setPreviewFile(file);
    setPreviewDialogOpen(true);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewContent(null);

    try {
      // When backend is available:
      // const response = await fetch(file.previewUrl);
      // const content = await response.text();
      // setPreviewContent(content);

      // For now, generate sample content based on file type
      const mimeType = file.mimeType || '';
      const fileName = file.name || '';

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      if (mimeType === 'text/csv' || fileName.endsWith('.csv')) {
        // Sample CSV content
        setPreviewContent(
          'SKU,Product Name,Quantity,Location,Status\n' +
          'SKU-001,Widget A,150,A-01-01,In Stock\n' +
          'SKU-002,Widget B,75,A-01-02,Low Stock\n' +
          'SKU-003,Gadget X,200,B-02-01,In Stock\n' +
          'SKU-004,Gadget Y,0,B-02-02,Out of Stock\n' +
          'SKU-005,Component Z,500,C-03-01,In Stock\n' +
          'SKU-006,Part Alpha,25,C-03-02,Low Stock\n' +
          'SKU-007,Part Beta,180,D-04-01,In Stock\n' +
          'SKU-008,Assembly Kit,45,D-04-02,In Stock'
        );
      } else if (mimeType === 'application/json' || fileName.endsWith('.json')) {
        // Sample JSON content
        setPreviewContent(JSON.stringify({
          reportDate: "2026-01-20",
          location: "Main Distribution Center",
          summary: {
            totalSKUs: 1250,
            lowStockItems: 45,
            outOfStockItems: 12,
            pendingOrders: 89
          },
          topMovers: [
            { sku: "SKU-001", name: "Widget A", movement: 1500 },
            { sku: "SKU-003", name: "Gadget X", movement: 1200 }
          ]
        }, null, 2));
      } else if (mimeType.startsWith('text/') || fileName.endsWith('.txt')) {
        // Sample text content
        setPreviewContent(
          'Daily Report - January 20, 2026\n' +
          '==========================================\n\n' +
          'Summary:\n' +
          '- Total orders processed: 245\n' +
          '- Items shipped: 1,890\n' +
          '- Receiving completed: 12 shipments\n' +
          '- Inventory adjustments: 8\n\n' +
          'Notes:\n' +
          'All operations running smoothly. No critical issues reported.'
        );
      } else if (mimeType === 'application/pdf') {
        // For PDF, use the preview URL directly
        setPreviewContent(file.previewUrl || file.downloadUrl);
      } else if (mimeType.startsWith('image/')) {
        // For images, use the preview URL directly
        setPreviewContent(file.previewUrl || file.downloadUrl);
      } else {
        setPreviewError('Preview not available for this file type');
      }
    } catch (error) {
      console.error('Failed to load file preview:', error);
      setPreviewError('Failed to load file preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewDialogOpen(false);
    setPreviewFile(null);
    setPreviewContent(null);
    setPreviewError(null);
  };

  const handleCreateFolder = async (parentId, folderName) => {
    if (!driveData) return;

    // Look up parent folder to get its S3 path
    const parentFolder = driveData.items.find(item => item.id === parentId);
    const parentPath = parentFolder?.folderPath || '';

    // Build the new folder's S3 path
    const newFolderPath = parentPath ? `${parentPath}/${folderName}` : folderName;

    console.log('Create folder:', folderName, 'at path:', newFolderPath);

    try {
      // POST /api/drive/folders - backend creates placeholder file at newFolderPath/.folder
      // await ChatService.createFolder({ parentPath, folderName });
      // For now, just log the API call that would be made
      console.log('API call: POST /api/drive/folders', { parentPath, folderName });
    } catch (error) {
      console.error('Failed to create folder:', error);
    }

    // Update local state (optimistic update)
    const newFolder = {
      id: `folder-${newFolderPath.replace(/[\\\/]/g, '-')}`,
      name: folderName,
      type: 'FOLDER',
      parentId: parentId,
      folderPath: newFolderPath, // Store the S3 path for backend communication
      children: [],
      createdAt: new Date().toISOString(),
      modifiedAt: new Date().toISOString(),
    };

    // Update parent folder's children
    const updatedItems = driveData.items.map(item => {
      if (item.id === parentId && item.children) {
        return { ...item, children: [...item.children, newFolder.id] };
      }
      return item;
    });

    setDriveData({
      ...driveData,
      items: [...updatedItems, newFolder],
    });
  };

  const handleSelectSession = useCallback(async (session) => {
    setCurrentSession(session);
    await loadSessionMessages(session.sessionId);
    // Mobile always shows chat directly now -- see project_two_panel_layout.
  }, [loadSessionMessages]);

  const handleContextMenu = useCallback((event, session) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
    setSelectedSessionForMenu(session);
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
    setSelectedSessionForMenu(null);
  }, []);

  const handleDeleteSession = async () => {
    if (!selectedSessionForMenu) return;
    
    try {
      await ChatService.deleteSession(selectedSessionForMenu.sessionId);
      await loadSessions();
      
      // If deleted session was current, reset to new session
      if (currentSession?.sessionId === selectedSessionForMenu.sessionId) {
        handleNewSession();
      }
      
      setDeleteDialog(false);
      handleCloseContextMenu();
    } catch (error) {
      console.error('Error deleting session:', error);
      setError('Failed to delete session');
    }
  };

  const handleEditDescription = async () => {
    if (!selectedSessionForMenu || !editDescription.trim()) return;
    
    try {
      await ChatService.updateSessionDescription(selectedSessionForMenu.sessionId, editDescription.trim());
      await loadSessions();
      setEditDialog(false);
      handleCloseContextMenu();
      setEditDescription('');
    } catch (error) {
      console.error('Error updating session description:', error);
      setError('Failed to update session description');
    }
  };

  const handleSearchConversations = async () => {
    if (!searchQuery.trim()) return;

    try {
      const searchResults = await ChatService.searchConversations(searchQuery);
      setConversationSearchResults(Array.isArray(searchResults) ? searchResults : []);
    } catch (error) {
      console.error('Error searching conversations:', error);
      setError('Failed to search conversations');
    }
  };

  const handleCloseSearchDialog = () => {
    setSearchDialog(false);
    setSearchQuery('');
    setConversationSearchResults(null);
  };

  const handleSelectSearchResult = (session) => {
    handleSelectSession(session);
    handleCloseSearchDialog();
  };

  // Account dialog handlers
  const handleOpenAccountDialog = () => {
    // Pre-fill form with current user data
    setAccountForm({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      username: user?.username || '',
      tenantCode: user?.tenant?.tenantCode || '',
      warehouseCode: selectedSystem?.systemCode || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setAccountFormErrors({});
    setAccountDialogOpen(true);
  };

  const handleCloseAccountDialog = () => {
    setAccountDialogOpen(false);
    setAccountForm({
      firstName: '',
      lastName: '',
      username: '',
      tenantCode: '',
      warehouseCode: '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setAccountFormErrors({});
  };

  const handleAccountFormChange = (field) => (event) => {
    setAccountForm(prev => ({ ...prev, [field]: event.target.value }));
    // Clear error when user starts typing
    if (accountFormErrors[field]) {
      setAccountFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateAccountForm = () => {
    const errors = {};

    if (!accountForm.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    if (!accountForm.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    if (!accountForm.username.trim()) {
      errors.username = 'Username is required';
    }

    // Password validation only if user is trying to change password
    if (accountForm.newPassword || accountForm.confirmPassword) {
      if (!accountForm.currentPassword) {
        errors.currentPassword = 'Current password is required to change password';
      }
      if (accountForm.newPassword.length < 6) {
        errors.newPassword = 'New password must be at least 6 characters';
      }
      if (accountForm.newPassword !== accountForm.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    setAccountFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveAccount = async () => {
    if (!validateAccountForm()) return;

    setAccountSaving(true);
    try {
      const updateData = {
        firstName: accountForm.firstName.trim(),
        lastName: accountForm.lastName.trim(),
        username: accountForm.username.trim(),
      };

      // Tenant/warehouse code are ADMIN/FOYSONIS_DEV-only server-side (see
      // UserService.updateProfile) -- only send them when actually changed,
      // not just because the (read-only, for non-admins) field is prefilled
      // with the current value; sending an unchanged value would otherwise
      // make a non-admin's save fail with "Only a company admin can change
      // the tenant code" for a field they never touched.
      const isCompanyAdmin = hasRole('ADMIN') || hasRole('FOYSONIS_DEV');
      if (isCompanyAdmin) {
        const newTenantCode = accountForm.tenantCode.trim().toUpperCase();
        if (newTenantCode && newTenantCode !== (user?.tenant?.tenantCode || '')) {
          updateData.tenantCode = newTenantCode;
        }
        const newWarehouseCode = accountForm.warehouseCode.trim().toUpperCase();
        if (newWarehouseCode && newWarehouseCode !== (selectedSystem?.systemCode || '')) {
          updateData.warehouseCode = newWarehouseCode;
          updateData.connectedSystemId = selectedSystem?.id;
        }
      }

      // Only include password fields if user is changing password
      if (accountForm.newPassword) {
        updateData.currentPassword = accountForm.currentPassword;
        updateData.newPassword = accountForm.newPassword;
      }

      await AdminService.updateUserProfile(updateData);

      // Refresh the cached user from the server so a tenant/warehouse code
      // change (or name/username change) is reflected immediately -- same
      // pattern AuthContext's own background refresh-on-mount already uses
      // (fetchCurrentUser writes the fresh copy to localStorage itself).
      if (updateData.tenantCode || updateData.warehouseCode || updateData.firstName || updateData.lastName || updateData.username) {
        try {
          await AuthService.fetchCurrentUser();
          refreshUserFromStorage();
        } catch (refreshErr) {
          console.warn('Account saved, but refreshing the cached profile failed:', refreshErr);
        }
      }

      setAccountSnackbar({ open: true, message: 'Account updated successfully', severity: 'success' });
      handleCloseAccountDialog();
    } catch (error) {
      console.error('Error updating account:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update account';
      setAccountSnackbar({ open: true, message: errorMessage, severity: 'error' });

      if (error.response?.data?.field) {
        setAccountFormErrors(prev => ({ ...prev, [error.response.data.field]: errorMessage }));
      }
    } finally {
      setAccountSaving(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleDateString() + ' ' + new Date(timestamp).toLocaleTimeString();
  };

  // The old "Sessions Sidebar" (SessionsList) column was removed 2026-09-28
  // once My Prompts and History moved into chat-toolbar chips, leaving it
  // permanently empty. See project_two_panel_layout.

  const ChatArea = React.useMemo(() => {
    // Find currently active dashboard if on a dashboard tab
    const activeDashboard = openDashboards.find(d => d.dashboardId === activeContentTab);

    return (
    <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column', height: '100%', bgcolor: colors.background }}>
      {/* Content Tabs - show when dashboards are open */}
      <ContentTabs
        activeTab={activeContentTab}
        openDashboards={openDashboards}
        onTabChange={handleContentTabChange}
        onCloseDashboard={handleCloseDashboard}
        colors={colors}
      />

      {/* Dashboard view - show when a dashboard tab is active */}
      {activeDashboard && (
        <Dashboard
          dashboard={activeDashboard}
          colors={colors}
          onSave={handleSaveDashboard}
          onRefreshAll={handleRefreshAllPanels}
          onRefreshPanel={(panelId) => handleRefreshPanel(activeDashboard.dashboardId, panelId)}
          onRemovePanel={(panelId) => handleRemovePanel(activeDashboard.dashboardId, panelId)}
          onTableRowAction={handleTableRowAction}
          onTableBulkAction={handleTableBulkAction}
          onTableGlobalAction={handleTableGlobalAction}
          onTableLinkClick={handleTableLinkClick}
          onTableConfigure={handleTableConfigure}
          onTableFilterRemove={handleTableFilterRemove}
          onTableAddFilter={handleTableAddFilter}
          onChartActionClick={handleActionClick}
        />
      )}

      {/* Chat Messages Area - show when on chat tab */}
      {activeContentTab === 'chat' && (
      <Paper
        elevation={0}
        sx={{
          flexGrow: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: colors.background,
          border: 'none',
        }}
      >
        {/* Chat Toolbar */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.5,
          borderBottom: `1px solid ${colors.border}`,
          bgcolor: colors.surface || colors.background,
          minHeight: 36,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleNewSession}
              size="small"
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                borderColor: colors.border,
                color: colors.textSecondary,
                '&:hover': { borderColor: colors.accent, color: colors.accent },
              }}
            >
              New Chat
            </Button>
            {SESSION_HISTORY_ENABLED && (
              <IconButton
                ref={historyButtonRef}
                size="small"
                onClick={() => {
                  // fresh titles and message counts, including the chat just had (FH-233)
                  if (!showSessionsInChat) loadSessions();
                  setShowSessionsInChat(!showSessionsInChat);
                }}
                sx={{ color: showSessionsInChat ? colors.accent : colors.textMuted, p: 0.5 }}
                title="Session history"
              >
                <HistoryIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={() => setSearchDialog(true)}
              sx={{ color: colors.textMuted, p: 0.5 }}
              title="Search past conversations"
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => setPromptsAnchorEl(e.currentTarget)}
              sx={{ color: colors.textMuted, p: 0.5 }}
              title="My Prompts"
            >
              <BoltIcon sx={{ fontSize: 18, color: '#22c55e' }} />
            </IconButton>
          </Box>
          <Typography variant="caption" sx={{ color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
            {currentSession ? currentSession.description || 'Current Session' : ''}
          </Typography>
        </Box>
        {/* Session History Panel -- a Popover anchored to the History icon so
            it overlays the chat messages instead of pushing them down (it was
            an inline <Collapse> before, which visually looked "below" the
            chat's own content rather than covering it). See
            project_session_history_overlay_fix. */}
        <Popover
          open={showSessionsInChat}
          anchorEl={historyButtonRef.current}
          onClose={() => setShowSessionsInChat(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          PaperProps={{ sx: { width: 320, bgcolor: colors.surface, border: `1px solid ${colors.border}` } }}
        >
          <Box sx={{
            maxHeight: 400,
            overflow: 'auto',
            bgcolor: colors.surface || colors.background,
          }}>
            {sessions.length === 0 && (
              <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', p: 2, textAlign: 'center' }}>
                No previous sessions
              </Typography>
            )}
            {sessions.map((session) => (
              <Box
                key={session.sessionId}
                onClick={() => { handleSelectSession(session); setShowSessionsInChat(false); }}
                onContextMenu={(e) => handleContextMenu(e, session)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1.5,
                  py: 0.75,
                  cursor: 'pointer',
                  borderLeft: currentSession?.sessionId === session.sessionId
                    ? '3px solid #22c55e'
                    : '3px solid transparent',
                  bgcolor: currentSession?.sessionId === session.sessionId
                    ? 'rgba(34, 197, 94, 0.08)'
                    : 'transparent',
                  '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' },
                }}
              >
                <Box sx={{ overflow: 'hidden', flex: 1 }}>
                  <Typography variant="body2" sx={{
                    color: colors.textPrimary, fontSize: '0.8rem',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {session.description}
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>
                    {formatTimestamp(session.lastAccessedAt || session.lastUpdatedAt)} · {session.messageCount} msgs
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Popover>
        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            overflow: 'auto',
            p: 2,
          }}
        >
          {/* Task Status Indicator - shows when a script is running */}
          {taskStatus && !dismissedTaskIds.has(taskStatus.taskId) && (
            <TaskStatusIndicator
              status={taskStatus}
              themeMode={themeId}
              onCancel={() => {
                cancelTask('Cancelled by user');
              }}
              onDismiss={() => {
                setDismissedTaskIds(prev => new Set([...prev, taskStatus.taskId]));
              }}
            />
          )}

          <List sx={{ p: 0 }}>
            {messages.map((message, index) => (
              <React.Fragment key={message.id}>
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                    gap: 2,
                    py: 2,
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 'auto' }}>
                    {message.sender === 'user' ? (
                      <Avatar
                        sx={{
                          bgcolor: '#22c55e',
                          color: isDark ? '#09090b' : '#ffffff',
                          borderRadius: 0,
                        }}
                      >
                        <PersonIcon />
                      </Avatar>
                    ) : (
                      <Box
                        component="img"
                        src="/img/atom-icon.svg"
                        alt="Atom"
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 0,
                        }}
                      />
                    )}
                  </ListItemAvatar>
                  <Box
                    ref={(el) => { messageRefs.current[message.id] = el; }}
                    sx={{
                      maxWidth: '70%',
                      // CSS flex-item gotcha: default min-width is the content's
                      // intrinsic width, so a wide table inside the bubble forces
                      // the bubble to grow past 70%. minWidth:0 lets the bubble
                      // shrink, and overflow:hidden keeps the wide table scrollable
                      // inside (TableContainer has its own overflow:auto).
                      minWidth: 0,
                      overflow: 'hidden',
                      bgcolor: message.sender === 'user' ? colors.userMessageBg : colors.surface,
                      border: message.sender === 'user' ? `1px solid ${colors.borderLight}` : 'none',
                      borderLeft: message.sender === 'bot' ? '4px solid #22c55e' : 'none',
                      p: 2,
                      borderRadius: 0,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ color: message.sender === 'user' ? '#22c55e' : colors.textPrimary, fontWeight: message.sender === 'user' ? 500 : 700 }}>
                          {message.sender === 'user' ? 'You' : 'Atom'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {flaggedMessages.has(message.id) && (
                          <FlagIcon sx={{ color: '#ef4444', fontSize: 16 }} />
                        )}
                        <Typography variant="caption" sx={{ color: colors.textMuted }}>
                          {message.timestamp}
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      {message.sender === 'bot' ? (
                        <MessageRenderer
                          message={message}
                          colors={colors}
                          isDark={isDark}
                          wizardFormValues={wizardFormValues}
                          isWizardSubmitted={submittedWizards.has(message.id)}
                          fieldErrors={fieldErrors}
                          validatingFields={validatingFields}
                          onActionClick={handleActionClick}
                          onWizardFieldChange={handleWizardFieldChange}
                          onWizardFieldBlur={handleWizardFieldBlur}
                          onWizardScriptValidate={handleWizardScriptValidate}
                          onWizardAction={(action) => handleWizardAction(action, message)}
                          onTableRowAction={handleTableRowAction}
                          onTableBulkAction={handleTableBulkAction}
                          onTableGlobalAction={handleTableGlobalAction}
                          onTableLinkClick={handleTableLinkClick}
                          onTableConfigure={handleTableConfigure}
                          onTableFilterRemove={handleTableFilterRemove}
                          onTableAddFilter={handleTableAddFilter}
                          onRelatedEntityClick={handleRelatedEntityClick}
                          onPinToBoard={handlePinToBoard}
                          onFileDownload={handleFileDownload}
                          onFilePreview={handleFilePreview}
                          onViewTableInWorkspace={handleViewTableInWorkspace}
                        />
                      ) : (
                        <>
                          {/* User message text */}
                          {message.text && (
                            <Typography
                              variant="body2"
                              sx={{ color: colors.textPrimary, whiteSpace: 'pre-wrap' }}
                            >
                              {message.text}
                            </Typography>
                          )}
                          {/* User message attachments */}
                          {message.attachments && message.attachments.length > 0 && (
                            <Box sx={{ mt: message.text ? 1.5 : 0 }}>
                              <Typography variant="caption" sx={{ color: colors.textMuted, mb: 0.5, display: 'block' }}>
                                Attached files:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {message.attachments.map((attachment, idx) => (
                                  <Box
                                    key={idx}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.75,
                                      px: 1,
                                      py: 0.5,
                                      bgcolor: colors.surface,
                                      border: `1px solid ${colors.border}`,
                                      borderRadius: 0,
                                    }}
                                  >
                                    {attachment.type?.startsWith('image/') ? (
                                      <ImageIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                                    ) : attachment.type === 'application/pdf' || attachment.name?.endsWith('.pdf') ? (
                                      <PdfIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                                    ) : attachment.type === 'text/csv' || attachment.name?.endsWith('.csv') ? (
                                      <CsvIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                    ) : (
                                      <FileIcon sx={{ fontSize: 16, color: '#71717a' }} />
                                    )}
                                    <Typography variant="caption" sx={{ color: colors.textPrimary }}>
                                      {attachment.name}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                    {/* Actions for user messages */}
                    {message.sender === 'user' && message.text && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, pt: 1, borderTop: `1px solid ${colors.border}` }}>
                        <Tooltip title="Save to My Prompts" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleSaveToQuickMenu(message.text)}
                            sx={{
                              color: colors.textMuted,
                              p: 0.5,
                              '&:hover': { color: '#22c55e', bgcolor: 'rgba(34, 197, 94, 0.08)' },
                            }}
                          >
                            <BookmarkBorderIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy text" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyMessage(message)}
                            sx={{
                              color: colors.textMuted,
                              p: 0.5,
                              '&:hover': { color: colors.textPrimary, bgcolor: colors.surfaceElevated },
                            }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                    {/* Feedback buttons for bot messages */}
                    {message.sender === 'bot' && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5, pt: 1, borderTop: `1px solid ${colors.border}` }}>
                        {/* Turn counter */}
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.textMuted,
                            fontSize: '0.7rem',
                            mr: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          Turn {Math.floor(index / 2) + 1}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleThumbsUp(message)}
                          title="Helpful"
                          sx={{
                            color: colors.textMuted,
                            p: 0.5,
                            '&:hover': { color: '#22c55e', bgcolor: 'rgba(34, 197, 94, 0.08)' },
                          }}
                        >
                          <ThumbUpIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleThumbsDown(message)}
                          title={flaggedMessages.has(message.id) ? "Remove flag" : "Not helpful"}
                          sx={{
                            color: flaggedMessages.has(message.id) ? '#ef4444' : colors.textMuted,
                            bgcolor: flaggedMessages.has(message.id) ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
                            p: 0.5,
                            '&:hover': { color: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.15)' },
                          }}
                        >
                          <ThumbDownIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleCopyMessage(message)}
                          title="Copy text"
                          sx={{
                            color: colors.textMuted,
                            p: 0.5,
                            '&:hover': { color: colors.textPrimary, bgcolor: colors.surfaceElevated },
                          }}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleScreenshotMessage(message.id)}
                          title="Screenshot to clipboard"
                          sx={{
                            color: colors.textMuted,
                            p: 0.5,
                            '&:hover': { color: '#3b82f6', bgcolor: 'rgba(59, 130, 246, 0.08)' },
                          }}
                        >
                          <ScreenshotIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </ListItem>
              </React.Fragment>
            ))}
            {isLoading && (
              <ListItem sx={{ gap: 2 }}>
                <ListItemAvatar sx={{ minWidth: 'auto' }}>
                  <Box
                    component="img"
                    src="/img/atom-icon.svg"
                    alt="Atom"
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 0,
                    }}
                  />
                </ListItemAvatar>
                <Box
                  sx={{
                    bgcolor: colors.surface,
                    borderLeft: '4px solid #22c55e',
                    p: 2,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: colors.textPrimary, mb: 1, fontWeight: 700 }}>Atom</Typography>
                  <ChatTurnActivity colors={colors} accent="#22c55e" onGrow={scrollToBottom} />
                </Box>
              </ListItem>
            )}
          </List>
          {/* FH-272: background runs of this chat (a large build part), with their progress, under the latest message so they are on screen */}
          {backgroundRuns
            .filter((run) => !isBackgroundRunDone(run) && run.metadata?.sessionId && run.metadata.sessionId === currentSession?.sessionId)
            .map((run) => (
              <BackgroundRunCard key={run.taskId} run={run} planStatus={backgroundPlanStatuses[run.metadata?.planKey]} colors={colors} />
            ))}

          <div ref={messagesEndRef} />
        </Box>
      </Paper>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mx: 2, mt: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Input Area */}
      <Paper elevation={0} sx={{
        p: 2,
        bgcolor: colors.surface,
        borderTop: `1px solid ${colors.border}`,
        border: 'none',
        borderRadius: 0,
      }}>
        {/* Hidden file input for attachments */}
        <input
          type="file"
          ref={chatFileInputRef}
          onChange={handleFilesSelected}
          style={{ display: 'none' }}
          multiple
          accept=".csv,.json,.txt,.pdf,.xlsx,.xls,.doc,.docx,.png,.jpg,.jpeg,.gif,.zip"
        />

        {/* Attachment Preview Area */}
        {attachedFiles.length > 0 && (
          <Box sx={{
            mb: 1.5,
            p: 1.5,
            bgcolor: colors.background,
            border: `1px solid ${colors.border}`,
            borderRadius: 0,
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                {attachedFiles.length} file{attachedFiles.length > 1 ? 's' : ''} attached
              </Typography>
              <IconButton
                size="small"
                onClick={handleClearAttachments}
                title="Remove all"
                sx={{ color: colors.textMuted, p: 0.5, '&:hover': { color: '#ef4444' } }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {attachedFiles.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    bgcolor: colors.surface,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 0,
                  }}
                >
                  {getAttachmentIcon(file)}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.textPrimary,
                        fontSize: '0.8rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 150,
                      }}
                    >
                      {file.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.7rem' }}>
                      {formatAttachmentSize(file.size)}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveAttachment(index)}
                    sx={{ color: colors.textMuted, p: 0.25, '&:hover': { color: '#ef4444' } }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Input row: Text field + Mic + Send */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', position: 'relative' }}>
          {slashMenuOpen && slashMenuItems.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: '100%',
                left: 0,
                right: 0,
                mb: 1,
                bgcolor: colors.background,
                border: `1px solid ${colors.borderLight}`,
                borderRadius: 1,
                boxShadow: 3,
                maxHeight: 240,
                overflowY: 'auto',
                zIndex: 20,
              }}
            >
              {slashMenuItems.map((item, idx) => (
                <Box
                  key={slashMenuStage === 'command' ? item.command : `${item.code}-${item.viewId || 'root'}`}
                  onMouseDown={(e) => { e.preventDefault(); handleSlashSelect(item); }}
                  onMouseEnter={() => setSlashMenuIndex(idx)}
                  sx={{
                    px: 2,
                    py: 1,
                    cursor: 'pointer',
                    bgcolor: idx === slashMenuIndex ? colors.hoverBg : 'transparent',
                  }}
                >
                  {slashMenuStage === 'command' ? (
                    <>
                      <Box sx={{ fontWeight: 600, fontSize: '0.85rem', color: colors.textPrimary }}>
                        {item.label}
                      </Box>
                      {item.description && (
                        <Box sx={{ fontSize: '0.75rem', color: colors.textMuted }}>
                          {item.description}
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box sx={{ fontSize: '0.85rem', color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <Box component="span" sx={{ color: colors.textMuted }}>Workspaces</Box>
                      <Box component="span" sx={{ color: colors.textMuted }}>›</Box>
                      <Box component="span" sx={{ fontWeight: item.viewLabel ? 400 : 600 }}>{item.name}</Box>
                      {item.viewLabel && (
                        <>
                          <Box component="span" sx={{ color: colors.textMuted }}>›</Box>
                          <Box component="span" sx={{ fontWeight: 600 }}>{item.viewLabel}</Box>
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={4}
            value={inputMessage}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            onKeyDown={handleSlashMenuKeyDown}
            placeholder={attachedFiles.length > 0 ? "Add a message about your files..." : "Ask about inventory, product locations, shipments..."}
            disabled={isLoading}
            inputRef={inputRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0, alignSelf: 'flex-end', mb: 1 }}>
                  <Tooltip title="More actions" arrow>
                    <IconButton
                      onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                      size="small"
                      sx={{
                        color: colors.textMuted,
                        p: 0.5,
                        '&:hover': {
                          color: systemColor,
                          bgcolor: `${systemColor}14`,
                        },
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
              // Mic moved from a separate button after the text field into an
              // endAdornment (mirroring the "+" startAdornment above) -- requested
              // live 2026-10-06 so both live "on" the text box itself, one on each
              // side, instead of mic reading as a separate button floating next to it.
              endAdornment: (
                <InputAdornment position="end" sx={{ ml: 0, alignSelf: 'flex-end', mb: 1 }}>
                  <Tooltip title={isListening ? 'Stop listening' : 'Voice input'} arrow>
                    <IconButton
                      onClick={toggleListening}
                      size="small"
                      sx={{
                        color: isListening ? '#ef4444' : colors.textMuted,
                        p: 0.5,
                        animation: isListening ? 'pulse 1.5s infinite' : 'none',
                        '@keyframes pulse': {
                          '0%': { opacity: 1 },
                          '50%': { opacity: 0.6 },
                          '100%': { opacity: 1 },
                        },
                        '&:hover': {
                          color: isListening ? '#ef4444' : systemColor,
                          bgcolor: isListening ? 'rgba(239, 68, 68, 0.1)' : `${systemColor}14`,
                        },
                      }}
                    >
                      {isListening ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: colors.background,
                alignItems: 'flex-end',
                '& fieldset': {
                  borderColor: colors.borderLight,
                },
                '&:hover fieldset': {
                  borderColor: systemColor,
                },
                '&.Mui-focused fieldset': {
                  borderColor: systemColor,
                  borderWidth: '2px',
                },
              },
              '& .MuiInputBase-input': {
                color: colors.textPrimary,
                '&::placeholder': {
                  color: colors.textMuted,
                  opacity: 1,
                },
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleSendMessage}
            disabled={isLoading || (!inputMessage.trim() && attachedFiles.length === 0)}
            data-send-button
            sx={{
              minWidth: 'auto',
              px: 3,
              py: 1.5,
              bgcolor: '#22c55e',
              color: isDark ? '#09090b' : '#ffffff',
              '&:hover': {
                bgcolor: '#16a34a',
              },
              '&.Mui-disabled': {
                bgcolor: colors.surfaceElevated,
                color: colors.textMuted,
              },
            }}
          >
            <SendIcon />
          </Button>
        </Box>

      </Paper>
    </Box>
  );
  }, [currentSession, formatTimestamp, messages, backgroundRuns, backgroundPlanStatuses, isBackgroundRunDone, isLoading, error, inputMessage, isDark, colors, handleNewSession, handleSendMessage, handleKeyPress, setInputMessage, setSearchDialog, setError, messagesEndRef, systemColor, activeContentTab, openDashboards, handleContentTabChange, handleCloseDashboard, handleSaveDashboard, handleRefreshAllPanels, handleRefreshPanel, handleRemovePanel, handlePinToBoard, setAddMenuAnchor, llmMode, setLlmMode, handleSaveToQuickMenu, handleCopyMessage, showSessionsInChat, setShowSessionsInChat, sessions, handleSelectSession, handleContextMenu, slashMenuOpen, slashMenuItems, slashMenuIndex, slashMenuStage, handleInputChange, handleSlashSelect, handleSlashMenuKeyDown, setPromptsAnchorEl, handleViewTableInWorkspace]);

  // Blocking Terms & Conditions gate -- checked BEFORE facility setup (both
  // are blocking, non-dismissable first-login gates; terms comes first since
  // agreeing to use the Service at all is the more foundational step).
  // Re-gates on a terms version bump: termsVersion must match
  // CURRENT_TERMS_VERSION exactly, not just be non-null, so updating the
  // terms later re-prompts existing users without a new DB column.
  const termsAccepted = user?.termsAcceptedAt && user?.termsVersion === CURRENT_TERMS_VERSION;
  if (!termsAccepted) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: colors.background }}>
        <TermsAcceptanceModal
          open
          onAccept={() => refreshUserFromStorage()}
        />
      </Box>
    );
  }

  // Blocking facility-setup gate: while the status check is in flight, show
  // a spinner instead of the shell (avoids a flash of the app underneath);
  // once it resolves true, render only the modal -- not the shell behind it
  // -- since it must not be dismissable except by completing it.
  if (!needsSystemSelection && facilitySetupNeeded !== false) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: colors.background }}>
        {facilitySetupError ? (
          <Box sx={{ maxWidth: 480, p: 3 }}>
            <Alert severity="error">{facilitySetupError}</Alert>
            <Button onClick={() => { setFacilitySetupError(null); setFacilitySetupCheckAttempt((attempt) => attempt + 1); }}>Retry</Button>
            <Button onClick={logout}>Log out</Button>
          </Box>
        ) : facilitySetupNeeded === true ? (
          <FirstLoginFacilitySetupModal
            open
            userId={user?.id}
            connectedSystemId={selectedSystem?.id}
            onComplete={handleFacilitySetupComplete}
            onLogout={logout}
          />
        ) : (
          <CircularProgress sx={{ color: '#22c55e' }} />
        )}
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: colors.background }}>
        {/* Mobile Header */}
        <AppBar position="static" elevation={0} sx={{
          bgcolor: colors.background,
          borderBottom: `1px solid ${colors.border}`,
        }}>
          <Toolbar sx={{ position: 'relative' }}>
            <Box
              component="img"
              src={isDark ? '/img/foysonis-logo-dark.png' : '/img/foysonis-logo-light.png'}
              alt="Foysonis Logo"
              sx={{ height: 32, mr: 2 }}
            />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: colors.textPrimary }}>
              FOYSONIS
            </Typography>
            <Chip
              icon={<ViewModuleIcon sx={{ fontSize: 16 }} />}
              label="Menu"
              onClick={() => setWorkspaceMenuOpen(true)}
              size="small"
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                bgcolor: 'rgba(34, 197, 94, 0.12)',
                color: '#22c55e',
                fontWeight: 700,
                '& .MuiChip-icon': { color: '#22c55e' },
                '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.22)' },
              }}
            />
            {(user?.tenant || selectedSystem) && (
              <Box
                onClick={(e) => {
                  console.log('Toolbar clicked! warehouses:', warehouses.length, 'hasMultipleSystems:', hasMultipleSystems);
                  if (warehouses.length > 1 || hasMultipleSystems) {
                    setSystemSelectorAnchor(e.currentTarget);
                  } else {
                    console.log('Not enough warehouses/systems to show selector');
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: `${systemColor}20`,
                  border: `1px solid ${systemColor}`,
                  borderRadius: 0,
                  px: 1,
                  py: 0.25,
                  mr: 1,
                  gap: 0.5,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: `${systemColor}30`,
                  },
                }}>
                {user?.tenant?.name && (
                  <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 500, fontSize: '0.7rem' }}>
                    {user.tenant.name}
                  </Typography>
                )}
                {user?.tenant?.name && selectedSystem && (
                  <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.7rem' }}>/</Typography>
                )}
                {selectedWarehouse ? (
                  <>
                    <SystemIcon sx={{ fontSize: 14, color: systemColor }} />
                    <Typography variant="caption" sx={{ color: systemColor, fontWeight: 600, fontSize: '0.7rem' }}>
                      {selectedWarehouse.warehouseName || selectedWarehouse.warehouseCode}
                    </Typography>
                    {warehouses.length > 1 && (
                      <ArrowDropDownIcon sx={{ fontSize: 16, color: systemColor, ml: -0.5 }} />
                    )}
                  </>
                ) : selectedSystem && (
                  <>
                    <SystemIcon sx={{ fontSize: 14, color: systemColor }} />
                    <Typography variant="caption" sx={{ color: systemColor, fontWeight: 600, fontSize: '0.7rem' }}>
                      {selectedSystem.name}
                    </Typography>
                    {hasMultipleSystems && (
                      <ArrowDropDownIcon sx={{ fontSize: 16, color: systemColor, ml: -0.5 }} />
                    )}
                  </>
                )}
              </Box>
            )}
            {studioAllowed && (
              <IconButton
                onClick={() => window.open('/studio', '_blank')}
                sx={{
                  color: colors.textSecondary,
                  '&:hover': {
                    color: '#8b5cf6',
                  },
                }}
                aria-label="FAST Studio"
                title="Open FAST Studio in new tab"
              >
                <PsychologyIcon />
              </IconButton>
            )}
            <IconButton
              onClick={handleNotificationsClick}
              sx={{
                color: unreadNotificationCount > 0 ? '#f59e0b' : colors.textSecondary,
                '&:hover': {
                  color: '#f59e0b',
                },
              }}
              aria-label="Notifications"
              title="View notifications"
            >
              <Badge badgeContent={unreadNotificationCount} color="error" max={99}>
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <Menu
              anchorEl={notificationAnchorEl}
              open={Boolean(notificationAnchorEl)}
              onClose={() => setNotificationAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  width: 320,
                  maxWidth: '90vw',
                  maxHeight: 420,
                  bgcolor: colors.surface,
                  border: `1px solid ${colors.border}`,
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${colors.border}` }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.textPrimary }}>
                  Notifications
                </Typography>
                {notificationList.some(n => !n.isViewed) && (
                  <Button size="small" onClick={handleMarkAllNotificationsRead}>
                    Mark all read
                  </Button>
                )}
              </Box>
              {notificationsLoading ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <CircularProgress size={20} />
                </Box>
              ) : notificationList.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: colors.textMuted }}>
                    No notifications
                  </Typography>
                </Box>
              ) : (
                notificationList.map((n) => (
                  <MenuItem
                    key={n.id}
                    onClick={() => !n.isViewed && handleMarkNotificationRead(n)}
                    sx={{
                      whiteSpace: 'normal',
                      alignItems: 'flex-start',
                      py: 1.25,
                      borderLeft: n.isViewed ? '3px solid transparent' : `3px solid ${systemColor}`,
                      bgcolor: n.isViewed ? 'transparent' : `${systemColor}0F`,
                      '&:hover .notification-actions': { opacity: 1 },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: n.isViewed ? 400 : 600 }}>
                        {n.message}
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.textMuted }}>
                        {n.createdDate ? new Date(n.createdDate).toLocaleString() : ''}
                      </Typography>
                    </Box>
                    <Box className="notification-actions" sx={{ display: 'flex', gap: 0.5, ml: 1, opacity: { xs: 1, sm: 0.4 }, transition: 'opacity 0.15s' }}>
                      <Tooltip title={n.isViewed ? 'Mark as unread' : 'Mark as read'} arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            n.isViewed ? handleMarkNotificationUnread(n) : handleMarkNotificationRead(n);
                          }}
                        >
                          {n.isViewed ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteNotification(n);
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </MenuItem>
                ))
              )}
            </Menu>
            {/* Theme selector with 4 themes */}
            <ThemeSelector showMenu={true} size="medium" />
            <FontSizeSelector size="medium" />
            <IconButton
              onClick={handleOpenAccountDialog}
              sx={{ color: colors.textSecondary, ml: 0.5 }}
              aria-label="Account"
              title={user ? `Account: ${user.firstName} ${user.lastName}` : 'Account Settings'}
            >
              <AccountCircleIcon />
            </IconButton>
            <IconButton
              onClick={logout}
              sx={{ color: colors.textSecondary, ml: 0.5 }}
              aria-label="Logout"
              title={user ? `Logout ${user.displayName || user.username}` : 'Logout'}
            >
              <LogoutIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Keep the workspace mounted so menu actions always have a live ref; toggle visibility without losing tabs. */}
        <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', bgcolor: colors.background }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, borderBottom: `1px solid ${colors.border}` }}>
            <Button size="small" onClick={() => setMobileWorkspaceVisible(value => !value)}>
              {mobileWorkspaceVisible ? 'Show chat' : 'Show workspace'}
            </Button>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, display: mobileWorkspaceVisible ? 'none' : 'flex', flexDirection: 'column' }}>
            {ChatArea}
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, display: mobileWorkspaceVisible ? 'flex' : 'none', flexDirection: 'column' }}>
            <WorkspacePanel
              ref={workspacePanelRef}
              colors={colors}
              atomResponseResults={atomResponseResults}
              highlightedResultId={highlightedResultId}
              onHighlightHandled={() => setHighlightedResultId(null)}
            />
          </Box>
        </Box>

        {/* Context Menu and Dialogs */}
        {renderDialogsAndMenus()}
      </Box>
    );
  }

  // Using function declaration (not arrow function) so it gets hoisted
  // and can be called in the mobile view return above
  function renderDialogsAndMenus() {
    return (
    <>
      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem
          onClick={() => {
            setEditDescription(selectedSessionForMenu?.description || '');
            setEditDialog(true);
          }}
        >
          <EditIcon sx={{ mr: 1 }} />
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => setDeleteDialog(true)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Warehouse / System Selector Menu */}
      <Menu
        anchorEl={systemSelectorAnchor}
        open={Boolean(systemSelectorAnchor)}
        onClose={() => setSystemSelectorAnchor(null)}
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            border: `1px solid ${colors.border}`,
            minWidth: 220,
          }
        }}
      >
        {/* Warehouses section */}
        {warehouses.length > 0 && (
          <>
            <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${colors.border}` }}>
              <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>
                Warehouses
              </Typography>
            </Box>
            {warehouses.map((wh) => (
              <MenuItem
                key={wh.id}
                onClick={async () => {
                  if (selectedWarehouse?.id !== wh.id) {
                    // FH-96 FIXED 2026-10-12: used to fire-and-forget
                    // selectWarehouse(wh) and immediately proceed with the
                    // connected-system switch/session clear/tab refresh
                    // regardless of whether the backend actually accepted
                    // the warehouse switch. Now awaits it and bails out
                    // (error already surfaced via AuthContext's setError)
                    // if the backend rejected it, instead of leaving the
                    // app in a mismatched local-vs-backend warehouse state.
                    try {
                      await selectWarehouse(wh);
                    } catch (err) {
                      setSystemSelectorAnchor(null);
                      return;
                    }
                    // Also update connected system to match the warehouse
                    const matchingSystem = user?.connectedSystems?.find(s => s.id === wh.id);
                    if (matchingSystem) selectSystem(matchingSystem);
                    setCurrentSession(null);
                    setMessages([]);
                    // Open workspaces stay open (no close/keep prompt) -- just
                    // reload seamlessly under the newly selected warehouse.
                    workspacePanelRef.current?.refreshAllTabs();
                  }
                  setSystemSelectorAnchor(null);
                }}
                selected={selectedWarehouse?.id === wh.id}
                sx={{
                  py: 1.5,
                  '&.Mui-selected': {
                    bgcolor: `${systemColor}15`,
                    borderLeft: `3px solid ${systemColor}`,
                  },
                  '&:hover': { bgcolor: `${systemColor}10` },
                }}
              >
                <SystemIcon sx={{ fontSize: 18, color: systemColor, mr: 1.5 }} />
                <Box>
                  <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: selectedWarehouse?.id === wh.id ? 600 : 400 }}>
                    {wh.warehouseName || wh.warehouseCode}
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>
                    {wh.warehouseCode}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </>
        )}
        {/* Fallback: Connected Systems (when no warehouses) */}
        {warehouses.length === 0 && user?.connectedSystems?.map((system) => (
          <MenuItem
            key={system.id}
            onClick={() => {
              if (selectedSystem?.id !== system.id) {
                selectSystem(system);
                setCurrentSession(null);
                setMessages([]);
                // Open workspaces stay open (no close/keep prompt) -- just
                // reload seamlessly under the newly selected system.
                workspacePanelRef.current?.refreshAllTabs();
              }
              setSystemSelectorAnchor(null);
            }}
            selected={selectedSystem?.id === system.id}
            sx={{
              py: 1.5,
              '&.Mui-selected': {
                bgcolor: `${getSystemColor(system.id)}15`,
                borderLeft: `3px solid ${getSystemColor(system.id)}`,
              },
              '&:hover': { bgcolor: `${getSystemColor(system.id)}10` },
            }}
          >
            <SystemIcon sx={{ fontSize: 18, color: getSystemColor(system.id), mr: 1.5 }} />
            <Box>
              <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: selectedSystem?.id === system.id ? 600 : 400 }}>
                {system.systemName}
              </Typography>
              {system.systemCode && (
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  {system.systemCode}
                </Typography>
              )}
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
      >
        <DialogTitle>Delete Session</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this conversation? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteSession} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Description Dialog */}
      <Dialog
        open={editDialog}
        onClose={() => setEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rename Session</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Session Description"
            fullWidth
            variant="outlined"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value.slice(0, 50))}
            inputProps={{ maxLength: 50 }}
            helperText={`${editDescription.length}/50 characters`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button onClick={handleEditDescription} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Search Dialog -- searches message/description content across all of
          this user's conversations (GET /api/sessions/search), not just
          session titles. Results are now actually shown and clickable
          (previously computed then discarded, see the fix note on
          conversationSearchResults above). */}
      <Dialog
        open={searchDialog}
        onClose={handleCloseSearchDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Search Conversations</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Search for..."
            fullWidth
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearchConversations(); }}
            placeholder="Keywords work best, e.g. 'damage' or 'receiving'"
          />
          {conversationSearchResults !== null && (
            <Box sx={{ mt: 2 }}>
              {conversationSearchResults.length === 0 ? (
                <Typography variant="body2" sx={{ color: colors.textMuted, textAlign: 'center', py: 2 }}>
                  No conversations found for "{searchQuery}"
                </Typography>
              ) : (
                <>
                  <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mb: 1 }}>
                    {conversationSearchResults.length} result{conversationSearchResults.length === 1 ? '' : 's'}
                  </Typography>
                  <Box sx={{ maxHeight: 320, overflow: 'auto', border: `1px solid ${colors.border}`, borderRadius: 1 }}>
                    {conversationSearchResults.map((session) => (
                      <Box
                        key={session.sessionId}
                        onClick={() => handleSelectSearchResult(session)}
                        sx={{
                          p: 1.25,
                          cursor: 'pointer',
                          borderBottom: `1px solid ${colors.border}`,
                          '&:last-child': { borderBottom: 'none' },
                          '&:hover': { bgcolor: colors.hoverBg },
                        }}
                      >
                        <Typography variant="body2" sx={{
                          color: colors.textPrimary,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {session.description}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.textMuted }}>
                          {formatTimestamp(session.lastAccessedAt || session.lastUpdatedAt)} · {session.messageCount} messages
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSearchDialog}>Cancel</Button>
          <Button onClick={handleSearchConversations} variant="contained">
            Search
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog
        open={feedbackDialog}
        onClose={() => setFeedbackDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Help Us Improve</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            We're sorry this response wasn't helpful. Please tell us what went wrong so we can do better.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="What could be improved?"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="Please describe what was wrong or how we could improve..."
          />
          {feedbackMessage && (
            <Typography variant="caption" sx={{ color: colors.textMuted, mt: 1, display: 'block' }}>
              Response ID: {feedbackMessage.id}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeedbackDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmitFeedback} variant="contained">
            Submit Feedback
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={feedbackSnackbar}
        autoHideDuration={3000}
        onClose={() => setFeedbackSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setFeedbackSnackbar(false)}
          severity="success"
          sx={{ borderRadius: 0 }}
        >
          Thanks for your feedback! This helps us help you better.
        </Alert>
      </Snackbar>

      {/* Copy Snackbar */}
      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setCopySnackbar(false)}
          severity="info"
          sx={{ borderRadius: 0 }}
        >
          Response copied to clipboard
        </Alert>
      </Snackbar>

      {/* Pin to Dashboard Dialog */}
      <PinToDashboardDialog
        open={pinDialogOpen}
        onClose={() => {
          setPinDialogOpen(false);
          setPinDialogData(null);
        }}
        onConfirm={handleConfirmPin}
        dashboards={savedDashboards}
        colors={colors}
        isLoading={pinDialogLoading}
        panelTitle={pinDialogData?.data?.table?.title || pinDialogData?.data?.chart?.title || ''}
      />

      {/* Configure Table Columns Dialog */}
      <ColumnConfigDialog
        open={columnConfigDialog.open}
        entityType={columnConfigDialog.entityType}
        colors={colors}
        onClose={() => setColumnConfigDialog({ open: false, tableId: null, entityType: null })}
        onSaved={() => setColumnConfigSnackbar(true)}
      />

      {/* Column Config Saved Snackbar */}
      <Snackbar
        open={columnConfigSnackbar}
        autoHideDuration={3000}
        onClose={() => setColumnConfigSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setColumnConfigSnackbar(false)} severity="success" sx={{ borderRadius: 0 }}>
          Column preferences saved — they'll apply the next time you load this table.
        </Alert>
      </Snackbar>

      {/* FH-272: a background run ended */}
      <Snackbar
        open={backgroundNotice.open}
        autoHideDuration={8000}
        onClose={() => setBackgroundNotice((n) => ({ ...n, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setBackgroundNotice((n) => ({ ...n, open: false }))}
          severity={backgroundNotice.severity}
          sx={{ borderRadius: 0, maxWidth: 640 }}
          data-testid="background-run-notice"
        >
          {backgroundNotice.message}
        </Alert>
      </Snackbar>

      {/* Dashboard Snackbar */}
      <Snackbar
        open={dashboardSnackbar.open}
        autoHideDuration={3000}
        onClose={() => setDashboardSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setDashboardSnackbar({ open: false, message: '' })}
          severity="success"
          sx={{ borderRadius: 0 }}
        >
          {dashboardSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Quick Menu Snackbar */}
      <Snackbar
        open={quickMenuSnackbar.open}
        autoHideDuration={3000}
        onClose={() => setQuickMenuSnackbar({ open: false, message: '', severity: 'success' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setQuickMenuSnackbar({ open: false, message: '', severity: 'success' })}
          severity={quickMenuSnackbar.severity}
          sx={{ borderRadius: 0 }}
        >
          {quickMenuSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Add/Edit Quick Menu Item Dialog */}
      <Dialog
        open={editQuickMenuDialog}
        onClose={() => {
          setEditQuickMenuDialog(false);
          setIsAddingQuickMenuItem(false);
          setEditingQuickMenuItem(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            borderRadius: 0,
            border: `1px solid ${colors.border}`,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.border}` }}>
          {isAddingQuickMenuItem ? 'Add Prompt' : 'Edit Prompt'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Display Text (Button Label)"
            value={editQuickMenuForm.displayValue}
            onChange={(e) => setEditQuickMenuForm({ ...editQuickMenuForm, displayValue: e.target.value })}
            margin="normal"
            helperText="Short text shown on the button (max 50 characters)"
            inputProps={{ maxLength: 50 }}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: colors.textPrimary,
                '& fieldset': { borderColor: colors.border },
                '&:hover fieldset': { borderColor: colors.textSecondary },
              },
              '& .MuiInputLabel-root': { color: colors.textSecondary },
              '& .MuiFormHelperText-root': { color: colors.textMuted },
            }}
          />
          <TextField
            fullWidth
            label="Prompt Text"
            value={editQuickMenuForm.value}
            onChange={(e) => setEditQuickMenuForm({ ...editQuickMenuForm, value: e.target.value })}
            margin="normal"
            multiline
            rows={3}
            helperText="The full prompt text that will be sent when clicked"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: colors.textPrimary,
                '& fieldset': { borderColor: colors.border },
                '&:hover fieldset': { borderColor: colors.textSecondary },
              },
              '& .MuiInputLabel-root': { color: colors.textSecondary },
              '& .MuiFormHelperText-root': { color: colors.textMuted },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${colors.border}`, p: 2 }}>
          <Button
            onClick={() => {
              setEditQuickMenuDialog(false);
              setIsAddingQuickMenuItem(false);
              setEditingQuickMenuItem(null);
            }}
            sx={{ color: colors.textSecondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveQuickMenuEdit}
            variant="contained"
            sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}
          >
            {isAddingQuickMenuItem ? 'Add' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Menu (for [+] button) */}
      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={() => setAddMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            borderRadius: 0,
            border: `1px solid ${colors.border}`,
            minWidth: 200,
          },
        }}
      >
        {CHAT_FILE_UPLOAD_ENABLED && (
          <MenuItem
            onClick={() => {
              setAddMenuAnchor(null);
              handleAttachClick();
            }}
            sx={{
              color: colors.textPrimary,
              '&:hover': {
                bgcolor: colors.hoverBg,
              },
            }}
          >
            <ListItemIcon>
              <AttachFileIcon sx={{ color: '#3b82f6' }} />
            </ListItemIcon>
            <ListItemText
              primary="Upload Files"
              secondary="Attach files to your message"
              secondaryTypographyProps={{ sx: { fontSize: '0.7rem', color: colors.textMuted } }}
            />
          </MenuItem>
        )}
        <MenuItem
          onClick={handleOpenDrive}
          sx={{
            color: colors.textPrimary,
            '&:hover': {
              bgcolor: colors.hoverBg,
            },
          }}
        >
          <ListItemIcon>
            <SearchIcon sx={{ color: '#f59e0b' }} />
          </ListItemIcon>
          <ListItemText
            primary="Browse Knowledgebase"
            secondary="Upload SOPs, policies, guides — view and search documents"
            secondaryTypographyProps={{ sx: { fontSize: '0.7rem', color: colors.textMuted } }}
          />
        </MenuItem>
      </Menu>

      {/* Drive Explorer Dialog */}
      <DriveExplorer
        open={driveDialogOpen}
        onClose={() => setDriveDialogOpen(false)}
        driveData={driveData}
        colors={colors}
        onUpload={handleFileUpload}
        onDelete={handleFileDelete}
        onRename={handleFileRename}
        onDownload={handleFileDownload}
        onCreateFolder={handleCreateFolder}
        onShare={handleShare}
        onLoadFolder={handleLoadFolder}
        onSearch={handleDocumentSearch}
        organizationUsers={organizationUsers}
      />

      {/* File Preview Dialog */}
      <FilePreviewDialog
        open={previewDialogOpen}
        onClose={handleClosePreview}
        file={previewFile}
        fileContent={previewContent}
        isLoading={previewLoading}
        error={previewError}
        colors={colors}
        onDownload={handleFileDownload}
      />

      {/* FAST Studio now opens in separate tab at /studio */}

      {/* Account Settings Dialog */}
      <Dialog
        open={accountDialogOpen}
        onClose={handleCloseAccountDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            borderRadius: 0,
            border: `1px solid ${colors.border}`,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountCircleIcon sx={{ color: '#22c55e' }} />
            Account Settings
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="First Name"
                value={accountForm.firstName}
                onChange={handleAccountFormChange('firstName')}
                error={!!accountFormErrors.firstName}
                helperText={accountFormErrors.firstName}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    '& fieldset': { borderColor: colors.border },
                  },
                  '& .MuiInputBase-input': { color: colors.textPrimary },
                  '& .MuiInputLabel-root': { color: colors.textSecondary },
                }}
              />
              <TextField
                label="Last Name"
                value={accountForm.lastName}
                onChange={handleAccountFormChange('lastName')}
                error={!!accountFormErrors.lastName}
                helperText={accountFormErrors.lastName}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    '& fieldset': { borderColor: colors.border },
                  },
                  '& .MuiInputBase-input': { color: colors.textPrimary },
                  '& .MuiInputLabel-root': { color: colors.textSecondary },
                }}
              />
            </Box>
            <TextField
              label="Username"
              value={accountForm.username}
              onChange={handleAccountFormChange('username')}
              error={!!accountFormErrors.username}
              helperText={accountFormErrors.username}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  '& fieldset': { borderColor: colors.border },
                },
                '& .MuiInputBase-input': { color: colors.textPrimary },
                '& .MuiInputLabel-root': { color: colors.textSecondary },
              }}
            />
            <TextField
              label="Email"
              value={user?.email || ''}
              fullWidth
              InputProps={{ readOnly: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  '& fieldset': { borderColor: colors.border },
                },
                '& .MuiInputBase-input': { color: colors.textSecondary },
                '& .MuiInputLabel-root': { color: colors.textSecondary },
              }}
            />
            <Divider sx={{ my: 1, borderColor: colors.border }} />
            <Typography variant="subtitle2" sx={{ color: colors.textSecondary }}>
              Account Info
            </Typography>
            <TextField
              label="Company"
              value={user?.tenant?.name || ''}
              fullWidth
              InputProps={{ readOnly: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  '& fieldset': { borderColor: colors.border },
                },
                '& .MuiInputBase-input': { color: colors.textSecondary },
                '& .MuiInputLabel-root': { color: colors.textSecondary },
              }}
            />
            {/* Tenant Code / Warehouse Code -- moved here from the signup
                form 2026-10-01 (hidden from every new signup, auto-generated
                instead; a company admin sets them deliberately afterward,
                here). ADMIN/FOYSONIS_DEV see them editable; everyone else
                sees the same read-only treatment as Company above, since a
                regular user changing a platform-wide-unique identifier used
                in API headers/integrations would be surprising. Server-side
                gate is the real enforcement (UserService.updateProfile) --
                this is just matching UX to it. */}
            {(hasRole('ADMIN') || hasRole('FOYSONIS_DEV')) ? (
              <>
                <TextField
                  label="Tenant Code"
                  value={accountForm.tenantCode}
                  onChange={(e) => setAccountForm(prev => ({ ...prev, tenantCode: e.target.value.toUpperCase() }))}
                  fullWidth
                  inputProps={{ maxLength: 50 }}
                  helperText="Shows up in support requests, admin screens, and API headers -- unique across all Foysonis customers"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': { borderColor: colors.border },
                    },
                    '& .MuiInputBase-input': { color: colors.textPrimary },
                    '& .MuiInputLabel-root': { color: colors.textSecondary },
                    '& .MuiFormHelperText-root': { color: colors.textMuted },
                  }}
                />
                <TextField
                  label="Warehouse Code"
                  value={accountForm.warehouseCode}
                  onChange={(e) => setAccountForm(prev => ({ ...prev, warehouseCode: e.target.value.toUpperCase() }))}
                  fullWidth
                  inputProps={{ maxLength: 50 }}
                  helperText={`Applies to the currently-selected warehouse (${selectedSystem?.systemName || selectedSystem?.systemCode || 'none selected'})`}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': { borderColor: colors.border },
                    },
                    '& .MuiInputBase-input': { color: colors.textPrimary },
                    '& .MuiInputLabel-root': { color: colors.textSecondary },
                    '& .MuiFormHelperText-root': { color: colors.textMuted },
                  }}
                />
              </>
            ) : (
              <TextField
                label="Warehouse Code"
                value={selectedSystem?.systemCode || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    '& fieldset': { borderColor: colors.border },
                  },
                  '& .MuiInputBase-input': { color: colors.textSecondary },
                  '& .MuiInputLabel-root': { color: colors.textSecondary },
                }}
              />
            )}
            <Box>
              <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mb: 0.5 }}>
                {(user?.roles || []).length > 1 ? 'Roles' : 'Role'}
              </Typography>
              {/* Chips read more clearly than a comma-joined string once a
                  user has more than one role -- see
                  project_account_settings_show_roles_todo. Roles are global
                  to the user (user_roles has no connected_system_id/
                  tenant_id column), not scoped per connected system --
                  confirmed via schema, answering the earlier open question. */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {(user?.roles || []).length === 0 ? (
                  <Typography variant="body2" sx={{ color: colors.textMuted }}>No roles assigned</Typography>
                ) : (
                  user.roles.map((code) => (
                    <Chip
                      key={code}
                      label={code.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())}
                      size="small"
                      sx={{
                        borderRadius: 0,
                        bgcolor: 'rgba(34, 197, 94, 0.12)',
                        color: '#22c55e',
                        fontWeight: 600,
                        border: '1px solid rgba(34, 197, 94, 0.4)',
                      }}
                    />
                  ))
                )}
              </Box>
            </Box>
            <Box>
              <Button
                variant="text"
                size="small"
                startIcon={<GavelIcon fontSize="small" />}
                onClick={() => setTermsReviewOpen(true)}
                sx={{ color: colors.textSecondary, textTransform: 'none', pl: 0 }}
              >
                View Terms &amp; Conditions
              </Button>
            </Box>

            <Divider sx={{ my: 1, borderColor: colors.border }} />
            <Typography variant="subtitle2" sx={{ color: colors.textSecondary }}>
              Change Password (optional)
            </Typography>
            <TextField
              label="Current Password"
              type={showCurrentPassword ? 'text' : 'password'}
              value={accountForm.currentPassword}
              onChange={handleAccountFormChange('currentPassword')}
              error={!!accountFormErrors.currentPassword}
              helperText={accountFormErrors.currentPassword}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      edge="end"
                      sx={{ color: colors.textSecondary }}
                    >
                      {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  '& fieldset': { borderColor: colors.border },
                },
                '& .MuiInputBase-input': { color: colors.textPrimary },
                '& .MuiInputLabel-root': { color: colors.textSecondary },
              }}
            />
            <TextField
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              value={accountForm.newPassword}
              onChange={handleAccountFormChange('newPassword')}
              error={!!accountFormErrors.newPassword}
              helperText={accountFormErrors.newPassword}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                      sx={{ color: colors.textSecondary }}
                    >
                      {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  '& fieldset': { borderColor: colors.border },
                },
                '& .MuiInputBase-input': { color: colors.textPrimary },
                '& .MuiInputLabel-root': { color: colors.textSecondary },
              }}
            />
            <TextField
              label="Confirm New Password"
              type={showNewPassword ? 'text' : 'password'}
              value={accountForm.confirmPassword}
              onChange={handleAccountFormChange('confirmPassword')}
              error={!!accountFormErrors.confirmPassword}
              helperText={accountFormErrors.confirmPassword}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  '& fieldset': { borderColor: colors.border },
                },
                '& .MuiInputBase-input': { color: colors.textPrimary },
                '& .MuiInputLabel-root': { color: colors.textSecondary },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${colors.border}`, px: 3, py: 2 }}>
          <Button
            onClick={handleCloseAccountDialog}
            sx={{ color: colors.textSecondary, borderRadius: 0 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveAccount}
            variant="contained"
            disabled={accountSaving}
            sx={{
              bgcolor: '#22c55e',
              borderRadius: 0,
              '&:hover': { bgcolor: '#16a34a' },
            }}
          >
            {accountSaving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {termsReviewOpen && (
        <TermsAcceptanceModal
          open
          mode="review"
          onClose={() => setTermsReviewOpen(false)}
          acceptedAt={user?.termsAcceptedAt}
          acceptedVersion={user?.termsVersion}
        />
      )}

      {/* Account Snackbar */}
      <Snackbar
        open={accountSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setAccountSnackbar({ ...accountSnackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setAccountSnackbar({ ...accountSnackbar, open: false })}
          severity={accountSnackbar.severity}
          sx={{ borderRadius: 0 }}
        >
          {accountSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Full-tree workspace menu -- opened from the center-top chip, see project_sidebar_tab_level_pinning */}
      <WorkspaceMenuOverlay
        open={workspaceMenuOpen}
        onClose={() => setWorkspaceMenuOpen(false)}
        colors={colors}
        pinnedWorkspaces={pinnedWorkspaces}
        workspaceGroups={workspaceGroups}
        onOpenWorkspace={handleOpenWorkspace}
        onPinChanged={refreshPinnedWorkspaces}
      />

      {/* Saved-prompts picker -- opened from the chip in the chat toolbar,
          see project_prompts_moved_to_chat_panel */}
      <PromptsMenuPopover
        anchorEl={promptsAnchorEl}
        onClose={() => setPromptsAnchorEl(null)}
        colors={colors}
        items={userQuickMenuItems}
        onUse={(item) => { setInputMessage(item.sentence); recordQuickMenuUse(item); }}
        onSend={(item) => { handleQuickSend(item.sentence); recordQuickMenuUse(item); }}
        onEdit={(item) => { setPromptsAnchorEl(null); handleEditQuickMenuItem(item); }}
        onDelete={handleDeleteQuickMenuItem}
        onAdd={() => { setPromptsAnchorEl(null); handleAddQuickMenuItem(); }}
      />
    </>
    );
  }

  // Desktop Layout
  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: colors.background }}>
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{
        bgcolor: colors.background,
        borderBottom: `1px solid ${colors.border}`,
      }}>
        <Toolbar sx={{ position: 'relative' }}>
          <Box
            component="img"
            src={isDark ? '/img/foysonis-logo-dark.png' : '/img/foysonis-logo-light.png'}
            alt="Foysonis Logo"
            sx={{ height: 32, mr: 2 }}
          />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: colors.textPrimary }}>
            FOYSONIS
          </Typography>
          {(user?.tenant || selectedSystem) && (
            <Box
              onClick={(hasMultipleSystems || warehouses.length > 1) ? (e) => setSystemSelectorAnchor(e.currentTarget) : undefined}
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: `${systemColor}20`,
                border: `1px solid ${systemColor}`,
                borderRadius: 0,
                px: 1.5,
                py: 0.5,
                mr: 2,
                gap: 1,
                cursor: (hasMultipleSystems || warehouses.length > 1) ? 'pointer' : 'default',
                '&:hover': (hasMultipleSystems || warehouses.length > 1) ? {
                  bgcolor: `${systemColor}30`,
                } : {},
              }}>
              {user?.tenant?.name && (
                <>
                  <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 500 }}>
                    {user.tenant.name}
                  </Typography>
                  {selectedSystem && (
                    <Typography variant="body2" sx={{ color: colors.textMuted }}>/</Typography>
                  )}
                </>
              )}
              {selectedWarehouse ? (
                <>
                  <Typography variant="body2" sx={{ color: systemColor, fontWeight: 600 }}>
                    {selectedWarehouse.warehouseCode || selectedWarehouse.warehouseName}
                  </Typography>
                  {warehouses.length > 1 && (
                    <ArrowDropDownIcon sx={{ fontSize: 18, color: systemColor, ml: -0.5 }} />
                  )}
                </>
              ) : selectedSystem && (
                <>
                  <Typography variant="body2" sx={{ color: systemColor, fontWeight: 600 }}>
                    {selectedSystem.systemCode || selectedSystem.systemName}
                  </Typography>
                  {hasMultipleSystems && (
                    <ArrowDropDownIcon sx={{ fontSize: 18, color: systemColor, ml: -0.5 }} />
                  )}
                </>
              )}
            </Box>
          )}
          {studioAllowed && (
            <IconButton
              onClick={() => window.open('/studio', '_blank')}
              sx={{
                color: colors.textSecondary,
                '&:hover': {
                  color: '#8b5cf6',
                  bgcolor: 'rgba(139, 92, 246, 0.08)',
                },
              }}
              aria-label="FAST Studio"
              title="Open FAST Studio in new tab"
            >
              <PsychologyIcon />
            </IconButton>
          )}
          <IconButton
            onClick={handleNotificationsClick}
            sx={{
              color: unreadNotificationCount > 0 ? '#f59e0b' : colors.textSecondary,
              '&:hover': {
                color: '#f59e0b',
                bgcolor: 'rgba(245, 158, 11, 0.08)',
              },
            }}
            aria-label="Notifications"
            title="View notifications"
          >
            <Badge badgeContent={unreadNotificationCount} color="error" max={99}>
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <Menu
            anchorEl={notificationAnchorEl}
            open={Boolean(notificationAnchorEl)}
            onClose={() => setNotificationAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                width: 360,
                maxHeight: 480,
                bgcolor: colors.surface,
                border: `1px solid ${colors.border}`,
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${colors.border}` }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.textPrimary }}>
                Notifications
              </Typography>
              {notificationList.some(n => !n.isViewed) && (
                <Button size="small" onClick={handleMarkAllNotificationsRead}>
                  Mark all read
                </Button>
              )}
            </Box>
            {notificationsLoading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress size={20} />
              </Box>
            ) : notificationList.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: colors.textMuted }}>
                  No notifications
                </Typography>
              </Box>
            ) : (
              notificationList.map((n) => (
                <MenuItem
                  key={n.id}
                  onClick={() => !n.isViewed && handleMarkNotificationRead(n)}
                  sx={{
                    whiteSpace: 'normal',
                    alignItems: 'flex-start',
                    py: 1.25,
                    borderLeft: n.isViewed ? '3px solid transparent' : `3px solid ${systemColor}`,
                    bgcolor: n.isViewed ? 'transparent' : `${systemColor}0F`,
                    '&:hover .notification-actions': { opacity: 1 },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: n.isViewed ? 400 : 600 }}>
                      {n.message}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textMuted }}>
                      {n.createdDate ? new Date(n.createdDate).toLocaleString() : ''}
                    </Typography>
                  </Box>
                  <Box className="notification-actions" sx={{ display: 'flex', gap: 0.5, ml: 1, opacity: { xs: 1, sm: 0.4 }, transition: 'opacity 0.15s' }}>
                    <Tooltip title={n.isViewed ? 'Mark as unread' : 'Mark as read'} arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          n.isViewed ? handleMarkNotificationUnread(n) : handleMarkNotificationRead(n);
                        }}
                      >
                        {n.isViewed ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNotification(n);
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </MenuItem>
              ))
            )}
          </Menu>
          {/* Theme selector with 4 themes */}
          <ThemeSelector showMenu={true} size="medium" />
          <FontSizeSelector size="medium" />
          <IconButton
            onClick={handleOpenAccountDialog}
            sx={{ color: colors.textSecondary, ml: 1 }}
            aria-label="Account"
            title={user ? `Account: ${user.firstName} ${user.lastName}` : 'Account Settings'}
          >
            <AccountCircleIcon />
          </IconButton>
          <IconButton
            onClick={logout}
            sx={{ color: colors.textSecondary, ml: 1 }}
            aria-label="Logout"
            title={user ? `Logout ${user.displayName || user.username}` : 'Logout'}
          >
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Persistent horizontal workspace nav bar -- replaces the old
          center-top "Menu" chip + WorkspaceMenuOverlay Dialog for desktop
          (that Dialog stays wired up for the mobile layout above, where a
          permanent bar doesn't fit). See project_workspace_nav_bar_redesign. */}
      <WorkspaceNavBar
        colors={colors}
        pinnedWorkspaces={pinnedWorkspaces}
        workspaceGroups={workspaceGroups}
        onOpenWorkspace={handleOpenWorkspace}
        onPinChanged={refreshPinnedWorkspaces}
      />

      {/* Main Content - 2 Panel Layout (Chat + Workspace) -- the third
          "Sessions Sidebar" column was removed 2026-09-28 once its only
          two sections (My Prompts, History) moved into chat-toolbar chips,
          leaving it permanently empty. See project_two_panel_layout.
          Chat moved to the LEFT (was right) the same day -- this app's
          chat is the primary, agent-driven control surface (it can open/
          drive the workspace panel via clientCommands), so it gets the
          anchored-left position, with the workspace panel as the
          responsive right-side canvas. See project_chat_on_left. */}
      <Box sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden', bgcolor: colors.background }}>
        {/* Chat Area + Edge Toggle - Left */}
        {!chatCollapsed ? (
          <Box key="chat-expanded" ref={chatBoxRef} sx={{ display: 'flex', width: chatWidth, flexShrink: 0, overflow: 'hidden', position: 'relative' }}>
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {ChatArea}
            </Box>
            {/* Collapse toggle — vertically centered chevron */}
            <Box
              onClick={() => setChatCollapsed(true)}
              sx={{
                width: 16,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                borderRight: `1px solid ${colors.border}`,
                '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.1)' },
                transition: 'background-color 0.2s',
              }}
              title="Collapse chat"
            >
              <ChevronLeftIcon sx={{ fontSize: 14, color: colors.textMuted }} />
            </Box>
            {/* Resize handle — thin draggable bar, on chat's right edge now */}
            <Box
              onMouseDown={handleChatResizeStart}
              sx={{
                width: 6,
                flexShrink: 0,
                cursor: 'col-resize',
                bgcolor: isResizingChat ? '#22c55e' : 'transparent',
                '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.4)' },
                transition: 'background-color 0.15s',
              }}
            />
          </Box>
        ) : (
          /* Collapsed chat - thin edge toggle with centered expand chevron.
             key="chat-collapsed" (and "chat-expanded" on the other branch)
             forces React to unmount/remount across this ternary instead of
             reusing the same DOM node (both branches render a Box at the
             same tree position) -- real bug found live: without the keys,
             a leftover inline style.width from the drag-resize fix's direct
             DOM write (chatBoxRef) survived onto this branch's reused node,
             so "collapsed" visually stuck at the last dragged width with
             just the chevron floating in the leftover blank space. See
             project_chat_collapse_stale_inline_style_fix. */
          <Box
            key="chat-collapsed"
            onClick={() => setChatCollapsed(false)}
            onMouseDown={(e) => handleChatResizeStart(e, { fromCollapsed: true })}
            title="Expand chat (click, or drag to a specific width)"
            sx={{
              width: 16,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // 'pointer', not 'col-resize' -- a plain click (the primary, expected
              // action) is the common case; dragging to a specific width is the
              // secondary power-user path documented in the title tooltip above,
              // not what the cursor should advertise by default. Found live
              // 2026-10-06 alongside the zero-width click bug fixed in
              // handleChatResizeStart -- same root confusion, that this element
              // is dual-purpose (click OR drag) but only ever showed the drag cursor.
              cursor: 'pointer',
              bgcolor: colors.surface,
              borderRight: `1px solid ${colors.border}`,
              '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
              transition: 'background-color 0.2s',
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 14, color: colors.textMuted }} />
          </Box>
        )}

        {/* Workspace Panel - Right */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <WorkspacePanel
            ref={workspacePanelRef}
            colors={colors}
            atomResponseResults={atomResponseResults}
            highlightedResultId={highlightedResultId}
            onHighlightHandled={() => setHighlightedResultId(null)}
          />
        </Box>
      </Box>

      {/* Context Menu and Dialogs */}
      {renderDialogsAndMenus()}
    </Box>
  );
};

export default AtomApp;